import { IPaymentContract } from "@features/payment/ui";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { ISubscriptionContract } from "@features/subscription/model";
import { combine, createEffect, createEvent, createStore, sample } from "effector";
import { SubscriptionApi } from "@features/shared/api/subscription";
import { pending, reset, interval } from "patronum";
import {
    bidiIsolate,
    checkUpsellPurchased,
    checkUpsellStatus,
    formatMinorINRRule,
    getCurrencySymbol,
    getPlatform,
    isAwaitingPaymentOrderStatus,
    isUpsellAwaitingPayment,
    UpsellStatus
} from "@features/helpers";
import { SALE_PERCENT_DEFAULTS, upsellRegistry } from "@data/upsells/meta";
import { substituteViewDataVariables } from "@features/upsell/viewDataText";
import { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import type { FlagsClient } from "@features/flags-provider";
import {
    CheckPurchasedProductsResponse,
    PurchaseUpsellResponse,
    UpsellPlacement
} from "@features/shared/entities/subscription";
import {
    buildUpsell3dsReturnUrls,
    isUpsell3dsChallenge,
    isUpsellChargeVerdictPending,
    mergeUpsell3dsOrderIds,
    parseUpsell3dsOrderIds,
    Upsell3dsChallenge,
    Upsell3dsResult,
    UPSELL_3DS_ORDER_IDS_KEY,
    UPSELL_3DS_VERIFY_STATUS,
    UPSELL_CHARGE_VERDICT_POLL_INTERVAL_MS,
    UPSELL_CHARGE_VERDICT_POLL_MAX_TICKS
} from "@features/upsell/utils/threeDs";
import { IRouterContract } from "@features/shared/router";
import { contentLocaleChanged, getContentLocale } from "@features/shared/locale";
import {
    IUpsellData,
    IUpsellPriceItem,
    IUpsellViewData,
    IUpsellsPricesResponse
} from "@features/shared/entities/student";
import { IUserAuthContract } from "@features/student/auth/model";
import { IMetadataContract } from "@features/student/metadata/model";
import {
    SolidFormResponse,
    SolidgatePlan,
    TruegateChargeResponse
} from "@features/shared/entities/payment";
import { StudentApi } from "@features/shared/api/course-data.api";
import { IAppRouteUnitsContract } from "@features/app/route-units";
import { IAppContract } from "@features/app/model";
import { ISignInFormContract } from "@features/student/auth/ui/sign-in.form";
import { subscribeAgainRequested } from "@features/subscription/renewal/model";
import { IPlatformContextContract } from "@features/launch-context/model";
import { resolveUpsellApmMethod, type UpsellApmMethod } from "@features/upsell/utils/upi";
import {
    type ApmUpsellCheck,
    type AwaitedApmUpsellOrder,
    claimUpsellOrderSuccess,
    clearPendingApmUpsell,
    readPendingApmUpsell,
    rememberPendingApmUpsell,
    resolveAwaitedApmUpsellOrder
} from "@features/upsell/utils/pendingApmUpsell";
import { CREATIVE_FAMILIES, CREATIVE_UPSELLS } from "@features/upsell/creative";

const MAX_UPSELL_FAIL_ATTEMPTS = 5;
const UPSELL_FAILED_ATTEMPTS_KEY = "upsell_failed_attempts_count";
const UPSELL_FAILED_PAYMENT_IDS_KEY = "upsell_failed_payment_ids";

// Parses the persisted dedup structure for counted failed upsell charges.
// Current shape: Record<groupId, paymentId[]> — the full set of payment ids
// already counted towards the fail limit. Legacy shape (pre-fix) stored a
// single last paymentId per group (Record<groupId, string>), which broke
// dedup when two upsell variants of one group (e.g. claude_work_bundle and
// claude_work_bundle_sale) failed with different payment ids: each
// check-purchased pass saw the "other" id as new and re-incremented the
// counter. Legacy values are normalized to single-item arrays.
// Returns null when the metadata key is absent/corrupt (caller falls back).
const parseFailedPaymentIds = (raw: string | undefined): Record<string, string[]> | null => {
    if (!raw) return null;
    try {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
        const normalized: Record<string, string[]> = {};
        for (const [groupId, value] of Object.entries(parsed)) {
            if (typeof value === "string") {
                normalized[groupId] = value ? [value] : [];
            } else if (Array.isArray(value)) {
                normalized[groupId] = value.filter((id): id is string => typeof id === "string");
            }
        }
        return normalized;
    } catch {
        return null;
    }
};

// The fail counter is derived from the set of counted payment ids — one real
// charge attempt (one payment id) is counted exactly once.
const countFailedPaymentIds = (ids: Record<string, string[]>): number =>
    Object.values(ids).reduce((total, groupIds) => total + groupIds.length, 0);
const UPSELL_PROGRESS_KEY = "upsell_progress_index";
export const BUNDLE_UPSELL_ID = "bundle";
const BUNDLE_PLAN_ID = "b044628b-51ee-409b-b183-da2c20c6ded0";
const BUNDLE_TRUEGATE_TAG = "bundle";

// The Claude bundle also grants the 31,000+ AI Prompts (complete) pack, but that
// pack is intentionally NOT part of the Claude funnel sequence. Once the funnel
// finishes we append it to the student's upsells metadata so /upsell/bundle can
// price it and the pack banners appear — without ever showing it as a funnel step.
// Solidgate uses a fixed, currency-agnostic plan id (currency variants live inside
// the plan price list); ecommpay/truegate resolve their own plan id from the loaded
// plan catalog by the `30k_prompts_complete_pack` tag + subscription currency.
const COMPLETE_PACK_UPSELL_ID = "30k_prompts_complete_pack";
const COMPLETE_PACK_SOLIDGATE_PLAN_ID = "6fab9de9-7c12-4c20-985d-b6831b4aa5bf";
const COMPLETE_PACK_UPSELL_IDS = new Set([COMPLETE_PACK_UPSELL_ID, "30k_prompts_complete_pack_sale"]);
const CLAUDE_BUNDLE_UPSELL_IDS = new Set(["claude_work_bundle", "claude_work_bundle_sale"]);

// Complete Bundle A/B upsell: one $29.99 purchase granting all packs at once
// (Claude + complete 31k prompts + personal boost + AI tools). The backend keeps
// a `complete_bundle` entry (with its solidgate planId) appended to the END of
// the funnel sequence, but it is never shown as a step of its own:
// `applyCompleteBundleSequence` either swaps it IN PLACE of the third screen
// (600_prompts / 600_prompts_sale) — when neither of the first two funnel
// upsells is purchased — or drops it. The drama / film chains end on their own
// bundle, swapped the same way for their third screen, the income kit.
export const COMPLETE_BUNDLE_UPSELL_ID = "complete_bundle";
export const COMPLETE_BUNDLE_UPSELL_IDS = new Set([
    COMPLETE_BUNDLE_UPSELL_ID,
    ...CREATIVE_FAMILIES.map((family) => CREATIVE_UPSELLS[family].bundle)
]);
const COMPLETE_BUNDLE_PREREQ_IDS = [
    "claude_work_bundle",
    "claude_work_bundle_sale",
    "ai_tools",
    "ai_tools_sale",
    ...CREATIVE_FAMILIES.flatMap((family) => [CREATIVE_UPSELLS[family].skill, CREATIVE_UPSELLS[family].skillSale])
];
const COMPLETE_BUNDLE_REPLACED_IDS = new Set([
    "600_prompts",
    "600_prompts_sale",
    ...CREATIVE_FAMILIES.map((family) => CREATIVE_UPSELLS[family].incomeKit)
]);

const findCompleteBundleEntry = (upsells: IUpsellData[]): IUpsellData | undefined =>
    upsells.find((u) => COMPLETE_BUNDLE_UPSELL_IDS.has(u.upsellId));

const applyCompleteBundleSequence = (
    upsells: IUpsellData[],
    showCompleteBundle: boolean,
    // Direct visit to /upsell/complete_bundle: keep the entry (appended, not
    // swapped into the funnel) so the plan resolves and the page renders like any
    // other upsell opened by URL — mirrors the `bundle` inject.
    currentUpsellId: string | null
): IUpsellData[] => {
    const bundleEntry = findCompleteBundleEntry(upsells);
    if (!bundleEntry) return upsells;

    const withoutBundle = upsells.filter((u) => u.upsellId !== bundleEntry.upsellId);
    if (!bundleEntry.planId?.trim()) return withoutBundle;

    if (showCompleteBundle) {
        const replaceIndex = withoutBundle.findIndex((u) => COMPLETE_BUNDLE_REPLACED_IDS.has(u.upsellId));
        if (replaceIndex === -1) return [...withoutBundle, bundleEntry];

        return [
            ...withoutBundle.slice(0, replaceIndex),
            bundleEntry,
            ...withoutBundle.slice(replaceIndex).filter((u) => !COMPLETE_BUNDLE_REPLACED_IDS.has(u.upsellId))
        ];
    }

    if (currentUpsellId === bundleEntry.upsellId) return [...withoutBundle, bundleEntry];

    return withoutBundle;
};

const upsellsHadClaudeFunnel = (upsells: IUpsellData[]) =>
    upsells.some((u) => CLAUDE_BUNDLE_UPSELL_IDS.has(u.upsellId));

const upsellsHasCompletePack = (upsells: IUpsellData[]) =>
    upsells.some((u) => COMPLETE_PACK_UPSELL_IDS.has(u.upsellId));

const appendCompletePackForClaudeFunnel = (upsells: IUpsellData[], planId: string): IUpsellData[] => [
    ...upsells,
    { upsellId: COMPLETE_PACK_UPSELL_ID, planId }
];

// Resolve the complete-pack plan id to append after the Claude funnel, per payment
// system. Solidgate is a fixed, currency-agnostic id; truegate looks it up in its
// loaded catalog by tag + currency (truegate plans are per-currency). Returns ""
// when it can't be resolved (funnel then simply doesn't append the pack rather
// than writing a broken planId). Ecommpay is intentionally out of the Claude
// funnel, so it never reaches this resolver.
const resolveCompletePackPlanIdForFunnel = (params: {
    paymentSystem: string;
    metadataPaymentSystem?: string;
}): string => {
    const system = ["solidgate", "truegate"].includes(params.paymentSystem)
        ? params.paymentSystem
        : (params.metadataPaymentSystem ?? "");

    if (system === "solidgate") return COMPLETE_PACK_SOLIDGATE_PLAN_ID;

    // Truegate plans are per currency: the chain carries the catalog TAG and
    // /students/upsells-prices resolves it to the student's currency.
    if (system === "truegate") return COMPLETE_PACK_UPSELL_ID;

    return "";
};

const BUNDLE_PACK_TARGET_IDS: Record<string, Set<string>> = {
    personalBoost: new Set(["600_prompts", "600_prompts_sale"]),
    incomeAi: new Set([
        "30k_prompts_complete_pack",
        "30k_prompts_complete_pack_sale",
        "30k_prompts",
        "30k_prompts_sale"
    ]),
    aiTools: new Set(["ai_tools", "ai_tools_sale"])
};
const UPSELL_GROUPS: Record<string, string> = {
    "30k_prompts": "30k_prompts",
    "30k_prompts_sale": "30k_prompts",
    "30k_prompts_complete_pack": "30k_prompts_complete_pack",
    "30k_prompts_complete_pack_sale": "30k_prompts_complete_pack",
    ai_tools: "ai_tools",
    ai_tools_sale: "ai_tools",
    "600_prompts": "600_prompts",
    "600_prompts_sale": "600_prompts",
    claude_work_bundle: "claude_work_bundle",
    claude_work_bundle_sale: "claude_work_bundle",
    complete_bundle: "complete_bundle",
    ...Object.fromEntries(
        CREATIVE_FAMILIES.flatMap((family) => [
            [CREATIVE_UPSELLS[family].skill, CREATIVE_UPSELLS[family].skill],
            [CREATIVE_UPSELLS[family].skillSale, CREATIVE_UPSELLS[family].skill]
        ])
    )
};

const parseUpsellsMetadata = (raw?: string): IUpsellData[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

// Same safe-parse contract as parseUpsellsMetadata, for the per-upsell view
// overrides map (upsellId -> view data). Returns {} on malformed input.
export type IUpsellContext = { onboardingId: string; variant: string; professionUpsells: string };

const parseUpsellContextMetadata = (raw?: string): IUpsellContext | null => {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
        return {
            onboardingId: typeof parsed.onboardingId === "string" ? parsed.onboardingId : "",
            variant: typeof parsed.variant === "string" ? parsed.variant : "",
            professionUpsells: typeof parsed.professionUpsells === "string" ? parsed.professionUpsells : ""
        };
    } catch {
        return null;
    }
};

const parseUpsellViewDataMetadata = (raw?: string): Record<string, IUpsellViewData> => {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const UPSELLS_PAYMENT_SYSTEM_KEY = "upsells_payment_system";
// Kept separate from the `upsells` key on purpose: that one is a plain
// {upsellId, planId} array read from many places and its shape must not change.
const UPSELLS_VIEW_DATA_KEY = "upsells_view_data";
// Content locale the cached view data was fetched with. The chain itself stays
// frozen for the whole funnel, but the copy must follow the language picker, so
// this stamp is what tells us the cached blob is in the wrong language.
const UPSELLS_VIEW_DATA_LOCALE_KEY = "upsells_view_data_locale";
// Which sequence bucket the student fell into (the backend resolves it from the
// payment provider, onboarding and experiment flags) plus the funnel variant
// that selected the per-upsell content. Persisted because the chain is frozen:
// the request that resolved it runs once, long before most of its screens are
// shown, and analytics has to label every one of them.
const UPSELLS_CONTEXT_KEY = "upsells_context";

const shouldReloadUpsells = (metadata: Record<string, string | undefined>, currentPaymentSystem: string): boolean => {
    const savedPaymentSystem = metadata[UPSELLS_PAYMENT_SYSTEM_KEY];
    if (metadata.upsells && savedPaymentSystem && savedPaymentSystem !== currentPaymentSystem) {
        return true;
    }
    return false;
};

const hasResolvedBundlePackPlanIds = (upsells: IUpsellData[]): boolean =>
    Object.values(BUNDLE_PACK_TARGET_IDS).every((targetIds) =>
        upsells.some((upsell) => targetIds.has(upsell.upsellId) && Boolean(upsell.planId?.trim()))
    );

// Build the list of upsell ids to query when checking whether the whole bundle
// is already purchased (all pack ids present in metadata + the bundle id itself).
const buildBundleCheckUpsellIds = (upsells: IUpsellData[]): string[] => {
    const upsellIds = new Set<string>();

    for (const upsell of upsells) {
        for (const targetSet of Object.values(BUNDLE_PACK_TARGET_IDS)) {
            if (targetSet.has(upsell.upsellId)) {
                upsellIds.add(upsell.upsellId);
            }
        }

        if (CLAUDE_BUNDLE_UPSELL_IDS.has(upsell.upsellId)) {
            upsellIds.add(upsell.upsellId);
        }
    }

    upsellIds.add(BUNDLE_UPSELL_ID);
    upsellIds.add(COMPLETE_BUNDLE_UPSELL_ID);

    return [...upsellIds];
};

const isClaudeBundlePurchased = (data: CheckPurchasedProductsResponse): boolean =>
    [...CLAUDE_BUNDLE_UPSELL_IDS].some((upsellId) => {
        const status = checkUpsellPurchased(data, upsellId);
        return status === "Success" || status === "Processing";
    });

type UpsellPlacementContext = {
    planId: string;
    // The upsell being bought: the bundle placement is recognised by its id,
    // whatever plan the provider resolved it to.
    upsellId?: string | null;
    fromProfile: boolean | null;
    firstLoginFlag: boolean;
    isAssistantRoute: boolean;
    isAiToolsRoute: boolean;
    isLessonRoute: boolean;
    history: string[] | null;
};

const resolveUpsellPlacement = (context: UpsellPlacementContext): UpsellPlacement => {
    const prevUrl = context.history && context.history.length > 1 ? (context.history.at(-2) ?? "") : "";

    if (context.upsellId === BUNDLE_UPSELL_ID || context.planId === BUNDLE_PLAN_ID) return UpsellPlacement.Bundle;
    if (context.isAssistantRoute || context.isAiToolsRoute) return UpsellPlacement.Assistant;
    if (context.isLessonRoute) return UpsellPlacement.Lesson;
    if (context.firstLoginFlag) return UpsellPlacement.FirstLogin;
    if ((!prevUrl && !context.fromProfile) || prevUrl.includes("/auth/signin")) return UpsellPlacement.Link;
    if (Boolean(context.fromProfile)) return UpsellPlacement.PromptsLibrary;

    return UpsellPlacement.Link;
};

const currentUpsellIdChanged = createEvent<string | null>();
const upsellBought = createEvent();
const upsellUpiFormRequested = createEvent();
const upsellUpiFormReset = createEvent();
const upsellUpiPaymentStarted = createEvent();
const upsellUpiPaymentSuccessReceived = createEvent();
const upsellUpiPaymentSucceeded = createEvent();
// The failed order, when it is not the form's own (one the page waited on and
// found declined), and whether the attempt is counted already.
type UpsellApmPaymentFailure = { orderId?: string; counted?: boolean };
const apmPaymentFailureOf = (failure: UpsellApmPaymentFailure | void): UpsellApmPaymentFailure =>
    (failure as UpsellApmPaymentFailure | undefined) ?? {};
const upsellUpiPaymentFailed = createEvent<UpsellApmPaymentFailure | void>();
// The CPF / QR / UPI window went away without a verdict: closed before the
// request was submitted, or gone with the page.
const upsellUpiPaymentWindowClosed = createEvent();
// The page is shown (loaded, or back from the UPI / bank app): re-check an APM
// upsell payment whose success event may have been lost with an unloaded tab.
const upsellApmPaymentRecheckRequested = createEvent();
// The APM upsell page is shown, or its form was refused: ask whether the
// current upsell has an order the customer has not paid yet.
const upsellApmAwaitingCheckRequested = createEvent();
const upsellApmAwaitingPollStarted = createEvent();
const upsellApmAwaitingPollStopped = createEvent();
const upsellChargeSucceeded = createEvent<PurchaseUpsellResponse>();
const upsell3dsChallengeRequired = createEvent<Upsell3dsChallenge>();
const upsell3dsChallengeFinished = createEvent<Upsell3dsResult>();
const upsell3dsChallengeFailed = createEvent();
// The charge came back HTTP 200 but Solidgate already settled it as declined
// (order status auth_failed & co). Sandbox declines and part of production
// declines are asynchronous: the backend resolves them through the status API
// and reports them in `status` instead of an HTTP error, so they must take the
// same path as `chargeUpsellFx.fail` — not the success navigation.
const upsellChargeDeclined = createEvent<PurchaseUpsellResponse>();
const upsellChargeVerdictPending = createEvent<PurchaseUpsellResponse>();
// The list of orders waiting for a verdict ran empty: stops the background poll.
const upsellChargeVerdictPollFinished = createEvent();
const upsell3dsLateChallengeClosed = createEvent();
// Internal gated trigger for the actual charge effects. `upsellBought` stays
// public (analytics / failed-attempt tracking still listen to it), but the
// network charge only fires through this event after the bundle-purchase guard.
const upsellChargeRequested = createEvent();
const getAvailableSubscriptionTransitionsRequested = createEvent();
const updateSubscriptionButtonClicked = createEvent<string>();
const checkSubscriptionUpdateRequested = createEvent<string>();
const subscriptionUpdateSuccessModalOpened = createEvent();
const subscriptionUpdateSuccessModalClosed = createEvent();
const stopSubscriptionUpdatePolling = createEvent();
const clearSubscriptionUpdateData = createEvent();
const upsellPurchaseLimitReached = createEvent();
const bundleCheckStarted = createEvent();
// Re-validate bundle purchase status on demand (e.g. window focus / tab becomes
// visible) to catch a purchase made in another tab and block double-buying.
const bundlePurchaseRecheckRequested = createEvent();
// Fired after the Claude first-login funnel finishes; carries the upsells list
// with 30k_prompts_complete_pack appended for metadata + store sync.
const claudeFunnelCompletePackAppended = createEvent<IUpsellData[]>();
const upsellFailedAttemptRegistered = createEvent<{
    count: number;
    groupId: string;
    paymentId: string;
    failedPaymentIds: Record<string, string[]>;
    newFailedPaymentIds: Record<string, string[]>;
}>();

const chargeUpsellFx = createEffect<
    { productId: string; currency: string; purchasePageURL: string; placement: UpsellPlacement },
    PurchaseUpsellResponse
>();
// One-time APM upsell form (UPI Intent for INR, Pix QR for BRL) for students
// without a one-click token. The stores and events keep their UPI names.
const createUpsellUpiFormFx = createEffect<
    {
        upsellId: string;
        productId: string;
        method: UpsellApmMethod;
        currency: "INR" | "BRL";
        purchasePageURL: string;
        placement: UpsellPlacement;
    },
    SolidFormResponse
>();
const confirmUpsellUpiPaymentFx = createEffect<{ upsellId: string; orderId: string }, void>();
const recheckUpsellApmPaymentFx = createEffect<{ upsellId: string; orderId: string }, boolean>();
// Both paths above can report one payment; only the first one moves on.
const claimUpsellApmSuccessFx = createEffect<{ upsellId: string; orderId: string }, boolean>(({ orderId }) =>
    claimUpsellOrderSuccess(orderId)
);
const rememberPendingApmUpsellFx = createEffect(({ upsellId, orderId }: { upsellId: string; orderId: string }) =>
    rememberPendingApmUpsell({ upsellId, orderId, startedAt: Date.now() })
);
const clearPendingApmUpsellFx = createEffect(() => clearPendingApmUpsell());
// The order the wait was about ended unpaid: the tab no longer has a payment to
// look out for, unless it has started another one since.
const forgetPendingApmUpsellOrderFx = createEffect((orderId: string) => {
    if (readPendingApmUpsell()?.orderId === orderId) clearPendingApmUpsell();
});
// `awaited` is the order the page waits on before this check: when it is paid
// meanwhile, the page moves on as after any APM success.
const checkUpsellApmAwaitingFx = createEffect(
    async ({
        upsellId
    }: {
        upsellId: string;
        awaited: AwaitedApmUpsellOrder | null;
    }): Promise<ApmUpsellCheck & { response: CheckPurchasedProductsResponse }> => {
        const response = await SubscriptionApi.checkUpsellPurchased([upsellId]);
        const paymentId = response.productStatus[upsellId]?.paymentId || null;
        return {
            status: checkUpsellPurchased(response, upsellId),
            awaitingOrderId: isUpsellAwaitingPayment(response, upsellId) ? (paymentId ?? "") : null,
            paymentId,
            checkedAt: Date.now(),
            response
        };
    }
);
// Every answer of the wait's own check, raw: a declined order lands here first
// (the wait then asks for a fresh form, which hides the declined row), so the
// failed-attempt count and its analytics read it from here as from any check.
// Derived from the effect's result rather than called inside its handler: the
// app runs in a forked scope, and a unit called after an await in a handler
// runs outside that scope, unseen by the samples.
const upsellApmAwaitingChecked = checkUpsellApmAwaitingFx.doneData.map(({ response }) => response);
const chargeAppleUpsellFx = createEffect<{ productId: string }, PurchaseUpsellResponse>();
const getAvailableSubscriptionTransitionsFx = createEffect<void, SolidgatePlan[]>();
const checkSubscriptionUpdateStatusFx = createEffect<string, unknown>();
const registerEcommpayUpsellFx = createEffect<{ id: string; placement: UpsellPlacement }, PurchaseUpsellResponse>();
const registerTruegateUpsellFx = createEffect<{ planId: number; placement: UpsellPlacement }, TruegateChargeResponse>();
const firstLoginFlagReset = createEvent();
// Fired before firstLoginFlagReset when the first-login funnel ends so metadata
// append runs while firstLoginFlag is still true (same tick as reset would fail the filter).
const claudeFunnelMetadataAppendRequested = createEvent();
const resetUpsellProgress = createEvent();

const $upsells = createStore<IUpsellData[]>([]);
// The unified config response's items. Truegate builds its own per-currency
// metadata from them: the order is the weighted sequence, and each planId is
// the admin-assigned truegate plan (validated against the catalog before use).
const $configUpsellItems = createStore<IUpsellData[]>([]);
// Display prices for upsells wherever they are shown outside the student's own
// chain (the bundle page, the cancellation offer). Resolved live by the backend
// from the admin plan config and the provider catalog, never frozen in metadata.
// null until the first response lands.
const upsellPricesRequested = createEvent();
const getUpsellPricesFx = createEffect(() => StudentApi.getUpsellsPrices());
const $upsellPrices = createStore<IUpsellsPricesResponse | null>(null).on(
    getUpsellPricesFx.doneData,
    (_, prices) => prices
);
// The content locale the prices were last requested for (stamped at request
// time, so an in-flight request is not duplicated): the names in the payload
// are in that language, and a switch past it refetches.
// A failed request clears the stamp, so the next entry to a priced route
// asks again instead of keeping the empty map for the rest of the session.
const $upsellPricesLocale = createStore<string | null>(null)
    .on(getUpsellPricesFx, () => getContentLocale())
    .reset(getUpsellPricesFx.fail);
const $currentUpsellId = createStore<string | null>(null).on(currentUpsellIdChanged, (_, id) => id);
// The current screen's offer from the payload — price, old price, discount
// and display name resolved by the backend from plan metadata for the
// student's currency and language. null until the payload lands or when the
// upsell is absent from it; the screens then keep their own rules.
const $currentUpsellOffer = combine(
    { prices: $upsellPrices, currentUpsellId: $currentUpsellId },
    ({ prices, currentUpsellId }): IUpsellPriceItem | null =>
        currentUpsellId && prices ? (prices.items[currentUpsellId] ?? null) : null
);
// Admin-authored content overrides per upsell id, rebuilt from metadata exactly
// like $upsells, so they survive a page refresh mid-funnel.
const $upsellViewData = createStore<Record<string, IUpsellViewData>>({});
// Rebuilt from metadata like $upsellViewData, so it survives a refresh and is
// still available on the last screen of the chain.
const $upsellContext = createStore<IUpsellContext | null>(null, {
    // Metadata is re-parsed into a fresh object on every change; without this
    // the store would re-emit an equal value and re-send the user properties.
    updateFilter: (next, prev) =>
        next?.onboardingId !== prev?.onboardingId ||
        next?.variant !== prev?.variant ||
        next?.professionUpsells !== prev?.professionUpsells
});
// Fired once, when the unified endpoint hands back a freshly resolved chain.
// Analytics turns this into the "which sequence did the student land in" event;
// the payload carries everything that decided it, so the tracker needs no extra
// module dependency.
const upsellSequenceResolved = createEvent<{
    context: IUpsellContext;
    paymentSystem: string;
    upsells: string[];
}>();
const $currentUpsellViewData = combine(
    { viewData: $upsellViewData, currentUpsellId: $currentUpsellId },
    ({ viewData, currentUpsellId }) => (currentUpsellId ? (viewData[currentUpsellId] ?? null) : null)
);
// The screens read THIS store (exposed under the $currentUpsellViewData
// contract name): the raw blob with {{percent}}/{{price}} already substituted.
// percent = the override or the screen's own default; price mirrors what the
// payment blocks display. Screen-specific variables ({{time}}, {{amount}})
// stay with their fields. Filled by a sample in the module class (the payment
// stores live on its contract).
const $currentUpsellViewDataResolved = createStore<IUpsellViewData | null>(null);
const UPSELL_META_BY_ID = new Map(upsellRegistry.map((entry) => [entry.id, entry]));
const resolveDisplayViewData = (
    viewData: IUpsellViewData | null,
    upsellId: string | null,
    price: string | undefined,
    // The plan's own discount (plan metadata, via upsells-prices), else the
    // screen default — view_data carries no percent of its own any more.
    offerPercent?: number | null
): IUpsellViewData | null => {
    if (!viewData) return viewData;
    const entry = upsellId ? UPSELL_META_BY_ID.get(upsellId) : undefined;
    const defaults = entry ? SALE_PERCENT_DEFAULTS[entry.type] : undefined;
    const percent = offerPercent ?? (entry?.sale ? (defaults?.saleDefault ?? defaults?.default) : defaults?.default);
    return substituteViewDataVariables(viewData, { percent, price });
};
const $upsellUpiFormState = createStore<{
    upsellId: string;
    productId: string;
    response: SolidFormResponse;
} | null>(null)
    .on(createUpsellUpiFormFx.done, (_, { params, result }) => ({
        upsellId: params.upsellId,
        productId: params.productId,
        response: result
    }))
    .reset(upsellUpiFormReset);
const $upsellUpiFormResponse = combine(
    { formState: $upsellUpiFormState, currentUpsellId: $currentUpsellId },
    ({ formState, currentUpsellId }) => (formState?.upsellId === currentUpsellId ? formState.response : null)
);
const $upsellUpiFormProductId = combine(
    { formState: $upsellUpiFormState, currentUpsellId: $currentUpsellId },
    ({ formState, currentUpsellId }) => (formState?.upsellId === currentUpsellId ? formState.productId : null)
);
const $upsellUpiFormError = createStore<string | null>(null)
    .on(createUpsellUpiFormFx.failData, (_, error) =>
        error instanceof Error ? error.message : "Unable to load UPI payment"
    )
    .reset([currentUpsellIdChanged, upsellUpiFormRequested, upsellUpiFormReset]);
const $isUpsellUpiFormPending = createUpsellUpiFormFx.pending;
const $isUpsellUpiConfirmationPending = confirmUpsellUpiPaymentFx.pending;
const $upsellUpiConfirmationErrorState = createStore<{ orderId: string; message: string } | null>(null)
    .on(confirmUpsellUpiPaymentFx.fail, (_, { params, error }) => ({
        orderId: params.orderId,
        message: error instanceof Error ? error.message : "UPI payment confirmation is delayed"
    }))
    .reset([upsellUpiPaymentSuccessReceived, confirmUpsellUpiPaymentFx.done, upsellUpiFormReset]);
const $upsellUpiConfirmationError = combine(
    { errorState: $upsellUpiConfirmationErrorState, formResponse: $upsellUpiFormResponse },
    ({ errorState, formResponse }) =>
        errorState && errorState.orderId === formResponse?.order_id ? errorState.message : null
);
// An order of the current APM upsell the customer has not paid yet (the backend's
// `awaiting_payment`): a QR shown before a reload, closed, or opened in another
// tab. The backend refuses a new form until it is paid or expires, so the page
// shows the wait instead of the payment button and polls for the verdict. An
// order this tab started before a reload is waited on from the first render,
// before the server is asked (resolveAwaitedApmUpsellOrder).
const upsellApmLocalPaymentFound = createEvent<{ upsellId: string; orderId: string }>();
// The customer opened the payment window of the form's order (clicked the Pix
// / UPI button): the order is waited on while the window is open, so a verdict
// Solidgate reports meanwhile (a QR expired, or a sandbox order cancelled by
// hand) reaches the page; the form itself reports neither. The wait ends with
// the window (closed, replaced, failed, paid) or with the verdict.
const upsellApmPaymentWindowOpened = createEvent<{ upsellId: string; orderId: string }>();
const toAwaited = (awaiting: AwaitedApmUpsellOrder | null): AwaitedApmUpsellOrder | null =>
    awaiting ? { orderId: awaiting.orderId, localSince: awaiting.localSince, open: awaiting.open } : null;
const $upsellApmAwaitingState = createStore<({ upsellId: string } & AwaitedApmUpsellOrder) | null>(null)
    .on(checkUpsellApmAwaitingFx.done, (state, { params, result }) => {
        const isSameUpsell = state?.upsellId === params.upsellId;
        // A late answer about another upsell does not end the wait on this one.
        // A window left open on another upsell's page is gone with that page.
        if (state && !isSameUpsell && result.awaitingOrderId === null) return state.open ? null : state;
        // The wait as it is now, not as it was when the check was sent: a
        // payment found by the page meanwhile is not dropped by an older answer.
        const awaited = resolveAwaitedApmUpsellOrder(isSameUpsell ? toAwaited(state) : null, result);
        return awaited ? { upsellId: params.upsellId, ...awaited } : null;
    })
    .on(upsellApmLocalPaymentFound, (state, found) =>
        state?.upsellId === found.upsellId && state.localSince === null
            ? state
            : { ...found, localSince: Date.now() }
    )
    .on(upsellApmPaymentWindowOpened, (state, opened) =>
        state?.upsellId === opened.upsellId && state.orderId === opened.orderId
            ? state
            : { ...opened, localSince: null, open: true }
    )
    // The window's wait ends with the window. A wait the server confirmed
    // meanwhile (the request is out) goes on without it.
    .on([upsellUpiPaymentWindowClosed, upsellUpiFormReset, upsellUpiPaymentSucceeded], (state) =>
        state?.open ? null : state
    );
const $upsellApmAwaitingOrder = combine(
    { awaiting: $upsellApmAwaitingState, currentUpsellId: $currentUpsellId },
    ({ awaiting, currentUpsellId }) => (awaiting?.upsellId === currentUpsellId ? awaiting : null)
);
type PendingUpsellCharge = {
    response: PurchaseUpsellResponse;
    upsellId: string;
    // Poll ticks this order has waited; dropped at UPSELL_CHARGE_VERDICT_POLL_MAX_TICKS.
    ticks: number;
};

// Orders whose verdict is still being polled in the background — a list, not a
// slot: a fast user reaches the next Buy before Solidgate raises the challenge
// for the previous order, and every order must still get its bank dialog. Not
// part of `$isUpsellRegistrationPending` on purpose: the funnel never waits.
const $pendingUpsellCharges = createStore<PendingUpsellCharge[]>([]);
// Late challenges waiting for the modal, which shows one at a time.
const $upsell3dsChallengeQueue = createStore<Upsell3dsChallenge[]>([]);
const $isUpsellRegistrationPending = pending([
    chargeUpsellFx,
    registerEcommpayUpsellFx,
    registerTruegateUpsellFx,
    chargeAppleUpsellFx
]);
const $isAvailableSubscriptionTransitionsPending = pending([getAvailableSubscriptionTransitionsFx]);
const $upsell3dsChallenge = createStore<Upsell3dsChallenge | null>(null)
    .on(upsell3dsChallengeRequired, (_, challenge) => challenge)
    .reset([upsellChargeSucceeded, upsell3dsChallengeFailed, upsell3dsLateChallengeClosed]);
// Orders that went through a 3DS challenge; persisted in student metadata (see
// run()) so the fail counter keeps ignoring them after a reload or in another tab.
const $upsell3dsOrderIds = createStore<string[]>([]).on(upsell3dsChallengeRequired, (ids, challenge) =>
    mergeUpsell3dsOrderIds(ids, [challenge.orderId])
);
// Upsells whose latest order is not decided yet on this client: the verdict is
// still being polled, a bank dialog is queued, or one is open. Their purchase
// row may read `3ds_verify` (= "Fail" for ownership) meanwhile, so pack pages
// must treat them as in progress rather than failed — no paywall on top of the
// bank dialog, no "payment error" toast, no second charge from a Buy button.
const $upsellIdsAwaitingVerdict = combine(
    $pendingUpsellCharges,
    $upsell3dsChallengeQueue,
    $upsell3dsChallenge,
    (pendingCharges, queue, challenge) => {
        const ids = new Set<string>();
        for (const pendingCharge of pendingCharges) ids.add(pendingCharge.upsellId);
        for (const queued of queue) if (queued.upsellId) ids.add(queued.upsellId);
        if (challenge?.upsellId) ids.add(challenge.upsellId);
        return [...ids];
    }
);
// The raw order status is returned as well: `3ds_verify` maps to "Fail" for
// ownership purposes but means "still waiting" while the challenge is open.
const checkUpsell3dsStatusFx = createEffect<
    string,
    { status: UpsellStatus; orderStatus: string | null; verifyUrl: string | null; paymentId: string | null }
>(async (upsellId) => {
    const response = await SubscriptionApi.checkUpsellPurchased([upsellId]);
    const productStatus = response.productStatus[upsellId];
    return {
        status: checkUpsellPurchased(response, upsellId),
        orderStatus: productStatus?.orderStatus ?? null,
        verifyUrl: productStatus?.verifyUrl ?? null,
        paymentId: productStatus?.paymentId ?? null
    };
});
const { tick: upsell3dsStatusTick } = interval({
    timeout: 4000,
    start: upsell3dsChallengeRequired,
    stop: upsell3dsChallengeFinished
});
// One request for every pending order at once.
const checkPendingUpsellChargesFx = createEffect<string[], CheckPurchasedProductsResponse>((upsellIds) =>
    SubscriptionApi.checkUpsellPurchased(upsellIds)
);
// `start` is ignored while the interval already runs, so every new pending
// order may fire it; the poll stops once the list is empty.
const { tick: upsellChargeVerdictTick } = interval({
    timeout: UPSELL_CHARGE_VERDICT_POLL_INTERVAL_MS,
    start: upsellChargeVerdictPending,
    stop: upsellChargeVerdictPollFinished
});
const $fromProfile = createStore<boolean | null>(null);
const $firstLoginFlag = createStore(false).reset(firstLoginFlagReset);
const $firstLoginFlagLoaded = createStore(false).reset(firstLoginFlagReset);
const $isSubscriptionUpdateSuccessModalOpen = createStore(false)
    .on(subscriptionUpdateSuccessModalOpened, () => true)
    .on(subscriptionUpdateSuccessModalClosed, () => false);
const $isUpdateSubscriptionButtonVisible = createStore(true)
    .on(updateSubscriptionButtonClicked, () => false)
    .on(clearSubscriptionUpdateData, () => true);
const $upsellRequestError = createStore<string | null>(null);
const $upsellFailedAttemptsCount = createStore(0);
const $upsellFailedPaymentIds = createStore<Record<string, string[]>>({});
const $metadataLoaded = createStore(false);
const $isFirstEntryPending = createStore(false);

const handleUpdateSubscriptionsFx = createEffect(async (product_id: string) => {
    const response = await SubscriptionApi.updateSubscription(product_id);

    return response;
});

const getFirstLoginFlagFx = createEffect(() => {
    if (typeof window !== "undefined") return localStorage.getItem("first_login");
});

const enableFirstLoginFlowFx = createEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem("first_login", "true");
});

const upsellSequenceInitialized = createEvent();

const getFromProfileFlagFx = createEffect(() => {
    if (typeof window !== "undefined") {
        const prevUrl = localStorage.getItem("prevUrl");
        return prevUrl === null ? false : true;
    }

    return null;
});

const checkUpsellPurchasedFx = createEffect(async (upsellIds: string[]) => {
    const response = await SubscriptionApi.checkUpsellPurchased(upsellIds);

    return response;
});

const checkBundlePurchaseStatusFx = createEffect(async (upsellIds: string[]) => {
    return await SubscriptionApi.checkUpsellPurchased(upsellIds);
});

const checkCompleteBundlePrereqsFx = createEffect(async () => {
    return await SubscriptionApi.checkUpsellPurchased(COMPLETE_BUNDLE_PREREQ_IDS);
});

// Same request, separate effect on purpose: only a check started AFTER a charge
// attempt may clear the optimistic "purchase started" flag. A response from the
// initial check could have been in flight since before the click and would
// wrongly clear it.
const recheckCompleteBundlePrereqsFx = createEffect(async () => {
    return await SubscriptionApi.checkUpsellPurchased(COMPLETE_BUNDLE_PREREQ_IDS);
});

// Server verdict: null = not checked yet (Complete Bundle stays hidden), true =
// neither of the first two funnel upsells is purchased or processing.
const $completeBundlePrereqsUnpurchasedByServer = createStore<boolean | null>(null);

// Optimistic local verdict: set the moment the user starts buying one of the
// first two funnel upsells. Kept SEPARATE from the server verdict on purpose —
// a recheck that started before the purchase resolves afterwards and would
// otherwise overwrite the flip, letting the funnel show the bundle to someone
// who just bought a prerequisite. Cleared by a post-charge recheck (which is
// authoritative — a declined card returns HTTP success but leaves the purchase
// unpaid) or by an outright charge failure.
const $completeBundlePrereqPurchaseStarted = createStore(false);

// null = still unknown (bundle stays hidden); false wins over a stale `true`.
const $completeBundlePrereqsUnpurchased = combine(
    $completeBundlePrereqsUnpurchasedByServer,
    $completeBundlePrereqPurchaseStarted,
    (byServer, purchaseStarted) => {
        if (purchaseStarted) return false;
        return byServer;
    }
);

const $bundleAllPacksPurchased = createStore(false);
const $isBundlePurchaseStatusResolved = createStore(false);
const $isBundlePurchaseCheckPending = pending([checkBundlePurchaseStatusFx]);
const $bundleCheckRequested = createStore(false).on(bundleCheckStarted, () => true);

getAvailableSubscriptionTransitionsFx.use(async () => {
    const response = await SubscriptionApi.getUpdateSubscriptionOptions();

    return response;
});

checkSubscriptionUpdateStatusFx.use(async (product_id: string) => {
    const response = await SubscriptionApi.checkSubscriptionUpdateSuccess(product_id as string);

    return response;
});

const $availableSubscriptionTransitions = createStore<SolidgatePlan[]>([]).on(
    getAvailableSubscriptionTransitionsFx.doneData,
    (_, data) => data as SolidgatePlan[]
);

const $subscriptionUpdateStatus = createStore<string>("").on(
    checkSubscriptionUpdateStatusFx.doneData,
    (_, data) => data as string
);

const { tick: subscriptionUpdatePollingTick, isRunning: $isSubscriptionUpdatePollingRunning } = interval({
    timeout: 5000,
    start: handleUpdateSubscriptionsFx.done,
    stop: stopSubscriptionUpdatePolling
});

const $subscriptionUpdateProductId = createStore<string | null>(null)
    .on(handleUpdateSubscriptionsFx.done, (_, { params }) => params)
    .reset([clearSubscriptionUpdateData, stopSubscriptionUpdatePolling]);

chargeUpsellFx.use(
    async ({
        productId,
        currency,
        purchasePageURL,
        placement
    }: {
        productId: string;
        currency: string;
        purchasePageURL: string;
        placement: UpsellPlacement;
    }) => {
        const platform = getPlatform();
        const origin = typeof window !== "undefined" ? window.location.origin : "";
        const response = await SubscriptionApi.purchaseUpsellProduct({
            productId,
            currency,
            platform,
            purchasePageURL,
            placement,
            ...buildUpsell3dsReturnUrls(origin)
        });

        return response;
    }
);

// Split the charge response: a 3DS challenge opens the modal, anything else is
// the purchase signal the navigation flow used to take from `doneData`.
sample({
    clock: chargeUpsellFx.doneData,
    source: $currentUpsellId,
    filter: (_, response) => isUpsell3dsChallenge(response),
    fn: (upsellId, response): Upsell3dsChallenge => ({
        orderId: response.order_id,
        verifyUrl: response.verify_url!,
        upsellId,
        response,
        late: false
    }),
    target: upsell3dsChallengeRequired
});

const isUpsellChargeDeclined = (response: PurchaseUpsellResponse): boolean =>
    typeof response.status === "string" && checkUpsellStatus(response.status, "none") === "Fail";

sample({
    clock: chargeUpsellFx.doneData,
    filter: (response) => !isUpsell3dsChallenge(response) && isUpsellChargeDeclined(response),
    target: upsellChargeDeclined
});

sample({
    clock: chargeUpsellFx.doneData,
    filter: (response) => !isUpsell3dsChallenge(response) && !isUpsellChargeDeclined(response),
    target: upsellChargeSucceeded
});

sample({
    clock: chargeUpsellFx.doneData,
    filter: (response) => !isUpsell3dsChallenge(response) && isUpsellChargeVerdictPending(response),
    target: upsellChargeVerdictPending
});

// Outside an upsell screen there is no upsell id to poll by: nothing to wait
// for (the interval then stops on its first tick, see below). A repeat charge
// of the same upsell replaces its older entry — the purchase row is one per
// upsell anyway.
sample({
    clock: upsellChargeVerdictPending,
    source: { pendingCharges: $pendingUpsellCharges, upsellId: $currentUpsellId },
    filter: (s) => s.upsellId !== null,
    fn: (s, response): PendingUpsellCharge[] => [
        ...s.pendingCharges.filter((pendingCharge) => pendingCharge.upsellId !== s.upsellId),
        { response, upsellId: s.upsellId!, ticks: 0 }
    ],
    target: $pendingUpsellCharges
});

// Every tick ages the pending orders and drops the ones past the give-up mark:
// the webhook settles those, and pack pages re-check ownership themselves, as
// before the 3DS flow existed. The aged list is the payload, so the request
// below reads the same list regardless of store update ordering.
const upsellChargeVerdictTicked = sample({
    clock: upsellChargeVerdictTick,
    source: $pendingUpsellCharges,
    fn: (pendingCharges): PendingUpsellCharge[] =>
        pendingCharges
            .map((pendingCharge) => ({ ...pendingCharge, ticks: pendingCharge.ticks + 1 }))
            .filter((pendingCharge) => pendingCharge.ticks < UPSELL_CHARGE_VERDICT_POLL_MAX_TICKS)
});
$pendingUpsellCharges.on(upsellChargeVerdictTicked, (_, pendingCharges) => pendingCharges);

sample({
    clock: upsellChargeVerdictTicked,
    source: checkPendingUpsellChargesFx.pending,
    filter: (isChecking, pendingCharges) => !isChecking && pendingCharges.length > 0,
    fn: (_, pendingCharges) => pendingCharges.map((pendingCharge) => pendingCharge.upsellId),
    target: checkPendingUpsellChargesFx
});

sample({
    clock: [upsellChargeVerdictTicked, $pendingUpsellCharges.updates],
    filter: (pendingCharges) => pendingCharges.length === 0,
    fn: () => undefined,
    target: upsellChargeVerdictPollFinished
});

// One pass over the answer for every pending order:
//  - 3ds_verify with verify_url: the order moved to 3ds_verify after the charge
//    request had returned, the backend stored the ACS URL and hands it out
//    here — the order joins the challenge queue;
//  - a purchase or a decline (any final status): nothing to show, the funnel
//    has moved on and the fail counter reads declines from the same check —
//    the order just leaves the list;
//  - no row yet, still processing, or 3ds_verify without a URL: keep waiting.
const upsellChargeVerdictsReceived = sample({
    clock: checkPendingUpsellChargesFx.doneData,
    source: $pendingUpsellCharges,
    fn: (pendingCharges, data) => {
        const challenges: Upsell3dsChallenge[] = [];
        const stillPending: PendingUpsellCharge[] = [];
        for (const pendingCharge of pendingCharges) {
            const productStatus = data.productStatus[pendingCharge.upsellId];
            const orderStatus = productStatus?.orderStatus ?? null;
            if (orderStatus === UPSELL_3DS_VERIFY_STATUS && productStatus?.verifyUrl) {
                challenges.push({
                    orderId: productStatus.paymentId ?? pendingCharge.response.order_id,
                    verifyUrl: productStatus.verifyUrl,
                    upsellId: pendingCharge.upsellId,
                    response: pendingCharge.response,
                    late: true
                });
                continue;
            }
            const status = checkUpsellPurchased(data, pendingCharge.upsellId);
            const isFinal =
                status === "Success" ||
                (status === "Fail" && orderStatus !== null && orderStatus !== UPSELL_3DS_VERIFY_STATUS);
            if (!isFinal) stillPending.push(pendingCharge);
        }
        return { challenges, stillPending };
    }
});
$pendingUpsellCharges.on(upsellChargeVerdictsReceived, (_, { stillPending }) => stillPending);
$upsell3dsChallengeQueue.on(upsellChargeVerdictsReceived, (queue, { challenges }) => [...queue, ...challenges]);

// The modal shows one challenge at a time: the next queued one opens as soon as
// the previous is finished or dismissed. The modal is mounted in the funnel and
// app layouts, so it opens on whatever screen the user is on by then.
sample({
    clock: [$upsell3dsChallengeQueue.updates, $upsell3dsChallenge.updates],
    source: { queue: $upsell3dsChallengeQueue, challenge: $upsell3dsChallenge },
    filter: (s) => s.challenge === null && s.queue.length > 0,
    fn: (s) => s.queue[0],
    target: upsell3dsChallengeRequired
});
$upsell3dsChallengeQueue.on(upsell3dsChallengeRequired, (queue, challenge) =>
    queue.filter((queued) => queued.orderId !== challenge.orderId)
);

sample({
    clock: upsell3dsStatusTick,
    source: { challenge: $upsell3dsChallenge, isPending: checkUpsell3dsStatusFx.pending },
    filter: (s) => s.challenge?.upsellId != null && !s.isPending,
    fn: (s) => s.challenge!.upsellId!,
    target: checkUpsell3dsStatusFx
});

sample({
    clock: checkUpsell3dsStatusFx.doneData,
    source: $upsell3dsChallenge,
    filter: (challenge, result) => challenge !== null && result.status === "Success",
    fn: (): Upsell3dsResult => "success",
    target: upsell3dsChallengeFinished
});

sample({
    clock: checkUpsell3dsStatusFx.doneData,
    source: $upsell3dsChallenge,
    filter: (challenge, result) =>
        challenge !== null && result.status === "Fail" && result.orderStatus !== UPSELL_3DS_VERIFY_STATUS,
    fn: (): Upsell3dsResult => "fail",
    target: upsell3dsChallengeFinished
});

sample({
    clock: upsell3dsChallengeFinished,
    source: $upsell3dsChallenge,
    filter: (challenge, result) => challenge !== null && !challenge.late && result === "success",
    fn: (challenge) => challenge!.response,
    target: upsellChargeSucceeded
});

sample({
    clock: upsell3dsChallengeFinished,
    source: $upsell3dsChallenge,
    filter: (challenge, result) => challenge !== null && !challenge.late && result !== "success",
    target: upsell3dsChallengeFailed
});

// A late challenge ends without side effects whatever the outcome: the funnel
// has already moved on, so no second navigation and no failed-purchase modal
// (whose Continue button navigates too). Ownership follows from the webhook.
sample({
    clock: upsell3dsChallengeFinished,
    source: $upsell3dsChallenge,
    filter: (challenge) => challenge !== null && challenge.late,
    fn: () => undefined,
    target: upsell3dsLateChallengeClosed
});

createUpsellUpiFormFx.use(async ({ productId, method, currency, purchasePageURL, placement }) => {
    return await SubscriptionApi.getSolidgateUpsellApmForm(method, {
        productId,
        currency,
        platform: getPlatform(),
        purchasePageURL,
        placement
    });
});

confirmUpsellUpiPaymentFx.use(async ({ upsellId }) => {
    const maxAttempts = 30;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const response = await SubscriptionApi.checkUpsellPurchased([upsellId]).catch(() => null);
        if (response) {
            const status = checkUpsellPurchased(response, upsellId);
            if (status === "Success" || status === "Processing") return;
        }

        await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    throw new Error("Payment confirmation timed out");
});

// Unlike the confirmation above (which follows a success event, so the payment
// went through), nothing says the customer paid: only "Success" counts, and the
// poll stops when the tab is hidden again.
recheckUpsellApmPaymentFx.use(async ({ upsellId }) => {
    for (let attempt = 0; attempt < 20; attempt += 1) {
        const response = await SubscriptionApi.checkUpsellPurchased([upsellId]).catch(() => null);
        if (response && checkUpsellPurchased(response, upsellId) === "Success") return true;
        if (typeof document !== "undefined" && document.visibilityState !== "visible") return false;
        await new Promise((resolve) => setTimeout(resolve, 3000));
    }
    return false;
});

sample({
    clock: upsellUpiPaymentSuccessReceived,
    source: {
        currentUpsellId: $currentUpsellId,
        formResponse: $upsellUpiFormResponse,
        confirmationPending: $isUpsellUpiConfirmationPending
    },
    filter: (state) =>
        state.currentUpsellId !== null && Boolean(state.formResponse?.order_id) && !state.confirmationPending,
    fn: (state) => ({ upsellId: state.currentUpsellId!, orderId: state.formResponse!.order_id! }),
    target: confirmUpsellUpiPaymentFx
});

sample({
    clock: confirmUpsellUpiPaymentFx.done,
    source: {
        currentUpsellId: $currentUpsellId,
        formResponse: $upsellUpiFormResponse
    },
    filter: (state, { params }) =>
        state.currentUpsellId === params.upsellId && state.formResponse?.order_id === params.orderId,
    fn: (_, { params }) => params,
    target: claimUpsellApmSuccessFx
});

sample({
    clock: upsellUpiPaymentStarted,
    source: { currentUpsellId: $currentUpsellId, formResponse: $upsellUpiFormResponse },
    filter: (state) => state.currentUpsellId !== null && Boolean(state.formResponse?.order_id),
    fn: (state) => ({ upsellId: state.currentUpsellId!, orderId: state.formResponse!.order_id! }),
    target: [rememberPendingApmUpsellFx, upsellApmPaymentWindowOpened]
});

sample({
    clock: upsellApmPaymentRecheckRequested,
    source: {
        currentUpsellId: $currentUpsellId,
        confirmationPending: $isUpsellUpiConfirmationPending,
        recheckPending: recheckUpsellApmPaymentFx.pending
    },
    filter: (state) =>
        state.currentUpsellId !== null &&
        !state.confirmationPending &&
        !state.recheckPending &&
        readPendingApmUpsell()?.upsellId === state.currentUpsellId,
    fn: (state) => ({ upsellId: state.currentUpsellId!, orderId: readPendingApmUpsell()?.orderId ?? "" }),
    target: recheckUpsellApmPaymentFx
});

sample({
    clock: recheckUpsellApmPaymentFx.done,
    source: $currentUpsellId,
    filter: (currentUpsellId, { params, result }) => result && currentUpsellId === params.upsellId,
    fn: (_, { params }) => params,
    target: claimUpsellApmSuccessFx
});

sample({
    clock: claimUpsellApmSuccessFx.done,
    source: $currentUpsellId,
    filter: (currentUpsellId, { params, result }) => result && currentUpsellId === params.upsellId,
    target: upsellUpiPaymentSucceeded
});

sample({
    clock: [upsellUpiPaymentSucceeded, upsellUpiPaymentFailed],
    target: clearPendingApmUpsellFx
});

const UPSELL_APM_AWAITING_POLL_INTERVAL_MS = 5000;

const { tick: upsellApmAwaitingPollTick } = interval({
    timeout: UPSELL_APM_AWAITING_POLL_INTERVAL_MS,
    start: upsellApmAwaitingPollStarted,
    stop: upsellApmAwaitingPollStopped,
    leading: false,
    trailing: false
});

// An upsell page opened in a tab that started a payment of it (a reload while
// the QR / UPI request was out): the wait shows at once, then the server is asked.
sample({
    clock: currentUpsellIdChanged,
    filter: (upsellId) => upsellId !== null && readPendingApmUpsell()?.upsellId === upsellId,
    fn: (upsellId) => ({ upsellId: upsellId!, orderId: readPendingApmUpsell()!.orderId }),
    target: upsellApmLocalPaymentFound
});

sample({
    clock: upsellApmLocalPaymentFound,
    fn: ({ upsellId, orderId }) => ({ upsellId, awaited: { orderId, localSince: Date.now() } }),
    target: checkUpsellApmAwaitingFx
});

// Asked when the page is shown and when the form is refused: the backend
// refuses one while an earlier order of the upsell can still be paid.
sample({
    clock: [upsellApmAwaitingCheckRequested, createUpsellUpiFormFx.fail],
    source: {
        currentUpsellId: $currentUpsellId,
        awaiting: $upsellApmAwaitingOrder,
        checkPending: checkUpsellApmAwaitingFx.pending
    },
    filter: (state) => state.currentUpsellId !== null && !state.checkPending,
    fn: (state) => ({ upsellId: state.currentUpsellId!, awaited: toAwaited(state.awaiting) }),
    target: checkUpsellApmAwaitingFx
});

sample({
    clock: upsellApmAwaitingPollTick,
    source: { awaiting: $upsellApmAwaitingOrder, checkPending: checkUpsellApmAwaitingFx.pending },
    filter: (state) => state.awaiting !== null && !state.checkPending,
    fn: (state) => ({ upsellId: state.awaiting!.upsellId, awaited: toAwaited(state.awaiting) }),
    target: checkUpsellApmAwaitingFx
});

sample({
    clock: $upsellApmAwaitingOrder,
    filter: (awaiting) => awaiting !== null,
    fn: () => undefined,
    target: upsellApmAwaitingPollStarted
});

sample({
    clock: $upsellApmAwaitingOrder,
    filter: (awaiting) => awaiting === null,
    fn: () => undefined,
    target: upsellApmAwaitingPollStopped
});

// Paid in the bank / UPI app: the page moves on as after any APM success.
sample({
    clock: checkUpsellApmAwaitingFx.done,
    filter: ({ params, result }) => Boolean(params.awaited?.orderId) && result.status === "Success",
    fn: ({ params }) => ({ upsellId: params.upsellId, orderId: params.awaited!.orderId }),
    target: claimUpsellApmSuccessFx
});

// Expired, declined or never sent: the wait ends, and a new form replaces the
// refused one right away.
sample({
    clock: checkUpsellApmAwaitingFx.done,
    source: $currentUpsellId,
    filter: (currentUpsellId, { params, result }) =>
        currentUpsellId === params.upsellId &&
        params.awaited !== null &&
        resolveAwaitedApmUpsellOrder(params.awaited, result) === null &&
        result.status !== "Success",
    fn: (_, { params }) => params.awaited!.orderId,
    target: [forgetPendingApmUpsellOrderFx, upsellUpiFormReset.prepend(() => undefined)]
});

// Declined while the page waited on it (a QR expired, or a sandbox order
// cancelled by hand): the purchase fails as after the form's own fail message,
// so the attempt is reported and the customer moves on, instead of a fresh
// form appearing without a word. The attempt itself is counted from the
// check's raw answer above. An order the server still lists as unpaid is no
// verdict.
sample({
    clock: checkUpsellApmAwaitingFx.done,
    source: $currentUpsellId,
    filter: (currentUpsellId, { params, result }) =>
        currentUpsellId === params.upsellId &&
        params.awaited !== null &&
        result.awaitingOrderId === null &&
        result.paymentId === params.awaited.orderId &&
        result.status === "Fail",
    fn: (_, { params }): UpsellApmPaymentFailure => ({ orderId: params.awaited!.orderId, counted: true }),
    target: upsellUpiPaymentFailed
});

chargeAppleUpsellFx.use(async ({ productId }: { productId: string }) => {
    return new Promise<PurchaseUpsellResponse>((resolve, reject) => {
        let existingUuid = null;

        if (typeof window !== "undefined" && window.NativeBridge?.purchase) {
            existingUuid = localStorage.getItem("user_public_uuid");
        }

        if (window.NativeBridge?.purchase && existingUuid) {
            const purchaseData = {
                productId: productId,
                appAccountToken: existingUuid
            };

            const handleSuccess = (event: any) => {
                window.removeEventListener("iosPurchaseSuccess", handleSuccess);
                window.removeEventListener("iosPurchaseError", handleError);

                resolve({
                    order_id: event.detail?.transactionId ?? "",
                    product_id: productId,
                    currency: event.detail?.currency ?? "USD",
                    type: "upsell",
                    payment_type: "apple_iap",
                    customer_account_id: existingUuid ?? "",
                    customer_email: "",
                    ip_address: "",
                    order_description: "",
                    platform: "apple"
                });
            };

            const handleError = (event: any) => {
                window.removeEventListener("iosPurchaseSuccess", handleSuccess);
                window.removeEventListener("iosPurchaseError", handleError);

                reject(new Error("Purchase failed"));
            };

            window.addEventListener("iosPurchaseSuccess", handleSuccess);
            window.addEventListener("iosPurchaseError", handleError);

            window.NativeBridge.purchase(JSON.stringify(purchaseData));

            setTimeout(() => {
                window.removeEventListener("iosPurchaseSuccess", handleSuccess);
                window.removeEventListener("iosPurchaseError", handleError);
                reject(new Error("Purchase timeout"));
            }, 15000);
        } else {
            reject(new Error("Native bridge not available"));
        }
    });
});

registerEcommpayUpsellFx.use(async ({ id, placement }: { id: string; placement: UpsellPlacement }) => {
    const response = await SubscriptionApi.purchaseEcommpayUpsellProduct(id, placement);

    return response;
});

registerTruegateUpsellFx.use(async ({ planId, placement }: { planId: number; placement: UpsellPlacement }) => {
    return await SubscriptionApi.purchaseTruegateUpsellProduct(planId, placement);
});

// Current Amplitude Experiment assignments as a plain map. Empty when the
// client hasn't initialized or resolved yet — the backend then picks the
// sequence without experiment context. `client.all()` reads the local cache
// and, unlike `client.variant()`, tracks no exposure events.
const getExperimentFlags = (client: FlagsClient | null): Record<string, string> => {
    if (!client) return {};
    const flags: Record<string, string> = {};
    for (const [key, variant] of Object.entries(client.all())) {
        if (typeof variant.value === "string" && variant.value.length > 0) {
            flags[key] = variant.value;
        }
    }
    return flags;
};

// The backend reads profession_upsells from the exposure-free snapshot above;
// variant() is called only to track the exposure ($feature_flag_called).
const trackProfessionUpsellsExposureFx = createEffect((client: FlagsClient) => {
    client.variant("profession_upsells");
});

// Unified upsell configuration, phase 1 (solidgate only): the sequence comes
// from /students/upsells-config, mapped to the exact `{upsellId, planId}` shape
// persisted to metadata, plus the per-upsell view overrides keyed by upsell id.
// Any failure or an empty sequence falls back to the legacy /students/upsells-
// sequence endpoint (which carries no view overrides), so behavior can never
// regress.
const getUpsellDataFx = createEffect(async (client: FlagsClient | null) => {
    // The locale the request is sent with (withContentParams reads the same
    // cookie in this very tick). Returned so the metadata stamp records what
    // the fetched content actually is — stamping getContentLocale() at
    // completion time lies whenever the user switches language mid-flight.
    const locale = getContentLocale();
    try {
        const config = await StudentApi.getUpsellsConfig(getExperimentFlags(client));
        if (config.items.length > 0) {
            const viewData: Record<string, IUpsellViewData> = {};
            for (const item of config.items) {
                if (item.viewData) viewData[item.upsellId] = item.viewData;
            }

            return {
                items: config.items.map(({ upsellId, planId }) => ({ upsellId, planId })),
                viewData,
                context: {
                    onboardingId: config.onboardingId,
                    variant: config.variant,
                    professionUpsells: config.professionUpsells
                } as IUpsellContext | null,
                locale
            };
        }
    } catch {
        // Fall through to the legacy endpoint.
    }

    // The legacy endpoint answers with the chain only — no sequence context.
    return { items: await StudentApi.getUpsellData(), viewData: {}, context: null as IUpsellContext | null, locale };
});

// The sequence alone — what every consumer of this effect needed before view
// overrides were added to the response.
const upsellDataReceived = getUpsellDataFx.doneData.map(({ items }) => items);

// Re-reads the config purely to refresh the localized copy after a language
// switch. It returns ONLY the view-data map: the caller must never touch the
// frozen `upsells` chain with it. On failure it throws, and the sample chain
// simply leaves the previously cached blob in place.
const refreshUpsellViewDataFx = createEffect(async (client: FlagsClient | null) => {
    // See getUpsellDataFx: the stamp must record the REQUEST's locale.
    const locale = getContentLocale();
    const config = await StudentApi.getUpsellsConfig(getExperimentFlags(client));
    const viewData: Record<string, IUpsellViewData> = {};
    for (const item of config.items) {
        if (item.viewData) viewData[item.upsellId] = item.viewData;
    }
    return { viewData, locale };
});

// Screen id saved next to the index. The index alone is ambiguous: the Complete
// Bundle swap is applied only on upsell routes, so the same index points at
// complete_bundle on-route and at 600_prompts off-route — exactly where the
// first-login restore runs. That mismatch sent users to 600_prompts, whose pack
// module bounced an already-owned pack to the library, which restored to
// 600_prompts again (redirect loop after browser Back from the bundle).
const UPSELL_PROGRESS_ID_KEY = "upsell_progress_upsell_id";

const saveUpsellProgressFx = createEffect(({ index, upsellId }: { index: number; upsellId: string }) => {
    localStorage.setItem(UPSELL_PROGRESS_KEY, String(index));
    localStorage.setItem(UPSELL_PROGRESS_ID_KEY, upsellId);
});

const clearUpsellProgressFx = createEffect(() => {
    localStorage.removeItem(UPSELL_PROGRESS_KEY);
    localStorage.removeItem(UPSELL_PROGRESS_ID_KEY);
});

// Where to resume the first-login funnel: the saved screen id when it is still a
// known screen (`knownUpsells` is the raw metadata chain, which keeps the
// complete_bundle entry), otherwise the saved index over `sequence`.
const resolveUpsellProgressPath = (knownUpsells: IUpsellData[], sequence: IUpsellData[]): string | null => {
    const savedId = localStorage.getItem(UPSELL_PROGRESS_ID_KEY);
    if (savedId && knownUpsells.some((u) => u.upsellId === savedId)) return `/upsell/${savedId}`;
    if (sequence.length === 0) return null;
    const saved = Number(localStorage.getItem(UPSELL_PROGRESS_KEY) ?? "0");
    const index = Math.min(Number.isNaN(saved) ? 0 : saved, sequence.length - 1);
    return `/upsell/${sequence[index].upsellId}`;
};

sample({
    clock: getFromProfileFlagFx.doneData,
    target: $fromProfile
});

sample({
    clock: getUpsellDataFx.failData,
    fn: (s) => JSON.stringify(s),
    target: $upsellRequestError
});

sample({
    clock: upsellPricesRequested,
    target: getUpsellPricesFx
});

// The upsell route waits for the first map before rendering (see
// upsellScreenGate.ts); a failed request hands it an empty one so the screens
// render without prices — and with the Skip button — instead of spinning
// forever.
sample({
    clock: getUpsellPricesFx.fail,
    fn: (): IUpsellsPricesResponse => ({ provider: "", currency: "", locale: getContentLocale(), items: {} }),
    target: $upsellPrices
});

const contract = new ModelContract(
    {
        $upsells,
        $currentUpsellId,
        $currentUpsellViewData: $currentUpsellViewDataResolved,
        $upsellContext,
        $upsellUpiFormResponse,
        $upsellUpiFormProductId,
        $upsellUpiFormError,
        $isUpsellUpiFormPending,
        $isUpsellUpiConfirmationPending,
        $upsellUpiConfirmationError,
        $upsellApmAwaitingOrder,
        $isUpsellRegistrationPending,
        $upsell3dsChallenge,
        $upsellIdsAwaitingVerdict,
        $fromProfile,
        $firstLoginFlag,
        $availableSubscriptionTransitions,
        $isAvailableSubscriptionTransitionsPending,
        $subscriptionUpdateStatus,
        $isSubscriptionUpdateSuccessModalOpen,
        $isSubscriptionUpdatePollingRunning,
        $subscriptionUpdateProductId,
        $isUpdateSubscriptionButtonVisible,
        $upsellRequestError,
        $upsellFailedAttemptsCount,
        $bundleAllPacksPurchased,
        $isBundlePurchaseCheckPending,
        $isBundlePurchaseStatusResolved,
        $isFirstEntryPending,
        $upsellPrices,
        $upsellPricesPending: getUpsellPricesFx.pending,
        $currentUpsellOffer
    },
    {
        currentUpsellIdChanged,
        upsellSequenceResolved,
        upsellBought,
        upsellUpiFormRequested,
        upsellUpiFormReset,
        upsellUpiPaymentStarted,
        upsellUpiPaymentSuccessReceived,
        upsellUpiPaymentSucceeded,
        upsellUpiPaymentFailed,
        upsellUpiPaymentWindowClosed,
        upsellApmPaymentRecheckRequested,
        upsellApmAwaitingCheckRequested,
        upsellChargeSucceeded,
        upsellChargeDeclined,
        upsellFailedAttemptRegistered,
        upsell3dsChallengeFinished,
        upsell3dsChallengeFailed,
        getAvailableSubscriptionTransitionsRequested,
        updateSubscriptionButtonClicked,
        checkSubscriptionUpdateRequested,
        subscriptionUpdateSuccessModalOpened,
        subscriptionUpdateSuccessModalClosed,
        stopSubscriptionUpdatePolling,
        clearSubscriptionUpdateData,
        upsellPurchaseLimitReached,
        firstLoginFlagReset,
        claudeFunnelMetadataAppendRequested,
        bundlePurchaseRecheckRequested,
        upsellPricesRequested
    },
    {
        chargeUpsellFx,
        createUpsellUpiFormFx,
        confirmUpsellUpiPaymentFx,
        recheckUpsellApmPaymentFx,
        checkUpsellApmAwaitingFx,
        chargeAppleUpsellFx,
        registerEcommpayUpsellFx,
        registerTruegateUpsellFx,
        checkUpsellPurchasedFx,
        getAvailableSubscriptionTransitionsFx,
        handleUpdateSubscriptionsFx,
        checkSubscriptionUpdateStatusFx
    },
    {}
);

export type IUpsellContract = typeof contract;

export class UpsellModule extends ModuleDescription<IUpsellContract> {
    constructor(
        public routesModule: IAppRouteUnitsContract,
        public appModule: IAppContract,
        public paymentModule: IPaymentContract,
        public subscriptionsModule: ISubscriptionContract,
        public routerModule: IRouterContract,
        public authModule: IUserAuthContract,
        public metadataModule: IMetadataContract,
        public signInFormModule: ISignInFormContract,
        public platformContextModule: IPlatformContextContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        // Leaving the page with the 3DS challenge open: the modal would be
        // re-mounted by another layout and the bank's iframe reloaded mid-flow,
        // leaving the order stuck in 3ds_verify. Treat navigation as dismissal.
        sample({
            clock: this.appModule.events.routePathNameChanged,
            source: $upsell3dsChallenge,
            filter: (challenge) => challenge !== null,
            fn: (): Upsell3dsResult => "closed",
            target: upsell3dsChallengeFinished
        });

        // The background verdict poll deliberately survives navigation: the
        // last funnel screen redirects to /home right after Buy, and a purchase
        // from a pack page lands on that page, so the challenge modal is
        // mounted in the app layout as well as in the funnel layout and opens
        // wherever the user is when Solidgate raises it.

        // Persist the 3DS order ids next to the failed-attempt ids so the fail
        // counter keeps ignoring them after a reload or in another tab.
        sample({
            clock: this.metadataModule.stores.$metadata,
            source: $upsell3dsOrderIds,
            fn: (ids, metadata) => mergeUpsell3dsOrderIds(ids, parseUpsell3dsOrderIds(metadata[UPSELL_3DS_ORDER_IDS_KEY])),
            target: $upsell3dsOrderIds
        });

        sample({
            clock: upsell3dsChallengeRequired,
            source: $upsell3dsOrderIds,
            fn: (ids, challenge) => ({
                key: UPSELL_3DS_ORDER_IDS_KEY,
                value: JSON.stringify(mergeUpsell3dsOrderIds(ids, [challenge.orderId]))
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            source: { path: this.routesModule.stores.upsell.$path, upsells: $upsells },
            filter: (s) => s.path !== null,
            fn: (s) => {
                const parts = s.path!.split("/");
                return parts[2] || null;
            },
            target: currentUpsellIdChanged
        });

        // A screen that is not part of the current sequence (e.g. 600_prompts
        // while the Complete Bundle swap is active, or a stale URL) must not
        // rewind the saved progress to the first screen — keep the last known
        // position instead.
        sample({
            clock: currentUpsellIdChanged,
            source: { upsells: $upsells, currentUpsellId: $currentUpsellId, firstLoginFlag: $firstLoginFlag },
            filter: (s) =>
                s.firstLoginFlag &&
                s.currentUpsellId !== null &&
                s.upsells.some((u) => u.upsellId === s.currentUpsellId),
            fn: (s) => ({
                index: s.upsells.findIndex((u) => u.upsellId === s.currentUpsellId),
                upsellId: s.currentUpsellId!
            }),
            target: saveUpsellProgressFx
        });

        sample({
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            filter: (s) => ["solidgate", "ecommpay", "truegate"].includes(s.paymentSystem),
            target: getFirstLoginFlagFx
        });

        sample({
            clock: getFirstLoginFlagFx.doneData,
            fn: (value) => value === "true",
            target: $firstLoginFlag
        });

        sample({
            clock: enableFirstLoginFlowFx.done,
            fn: () => true,
            target: $firstLoginFlag
        });

        sample({
            clock: getFirstLoginFlagFx.done,
            fn: () => true,
            target: $firstLoginFlagLoaded
        });

        // Solidgate only: truegate's chain is finalized by its per-currency
        // resolver below (raw config items may still contain steps that drop),
        // and its redirect/first-login flow runs off upsellSequenceInitialized.
        sample({
            clock: upsellDataReceived,
            source: {
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                isApp: this.platformContextModule.stores.$isApp,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            filter: (s, p) =>
                s.paymentSystem === "solidgate" &&
                !s.isApp &&
                !s.isUpsellRoute &&
                p !== null &&
                p.length > 0 &&
                s.subscriptions.length <= 1,
            fn: (_, p) => resolveUpsellProgressPath(p, p)!,
            target: this.routerModule.events.routerReplace
        });

        sample({
            clock: upsellDataReceived,
            source: {
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                isApp: this.platformContextModule.stores.$isApp,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            filter: (s, p) =>
                s.paymentSystem === "solidgate" &&
                !s.isApp &&
                !s.isUpsellRoute &&
                p !== null &&
                p.length > 0 &&
                s.subscriptions.length <= 1,
            target: enableFirstLoginFlowFx
        });

        sample({
            clock: [this.appModule.events.routePathNameChanged, $firstLoginFlag, $upsells],
            source: {
                upsells: $upsells,
                metadata: this.metadataModule.stores.$metadata,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                firstLoginFlag: $firstLoginFlag,
                isApp: this.platformContextModule.stores.$isApp
            },
            filter: (s) => !s.isApp && !s.isUpsellRoute && s.upsells.length > 0 && s.firstLoginFlag,
            // `$upsells` is the off-route (unswapped) chain here; the raw metadata
            // chain still knows complete_bundle, so a user who left from the
            // bundle screen resumes on it instead of on the swapped-out 600_prompts.
            fn: (s) => resolveUpsellProgressPath(parseUpsellsMetadata(s.metadata.upsells), s.upsells)!,
            target: this.routerModule.events.routerReplace
        });

        sample({
            clock: upsellDataReceived,
            source: { paymentSystem: this.subscriptionsModule.stores.$paymentSystem },
            filter: (src, s) => src.paymentSystem === "solidgate" && s !== null,
            fn: (_, s) => ({
                key: "upsells",
                value: JSON.stringify(s)
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: upsellDataReceived,
            source: { paymentSystem: this.subscriptionsModule.stores.$paymentSystem },
            filter: (src, s) => src.paymentSystem === "solidgate" && s !== null,
            fn: () => ({
                key: UPSELLS_PAYMENT_SYSTEM_KEY,
                value: "solidgate"
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        // Truegate consumes the same response through its resolver below.
        sample({
            clock: upsellDataReceived,
            target: $configUpsellItems
        });

        // Persisted next to the sequence so a refresh mid-funnel restores the
        // admin-authored content along with the screens it belongs to.
        sample({
            clock: getUpsellDataFx.doneData,
            fn: ({ viewData }) => ({
                key: UPSELLS_VIEW_DATA_KEY,
                value: JSON.stringify(viewData)
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: getUpsellDataFx.doneData,
            fn: ({ locale }) => ({ key: UPSELLS_VIEW_DATA_LOCALE_KEY, value: locale }),
            target: this.metadataModule.events.setMetadataRequested
        });

        // Only the unified endpoint knows the sequence context; the legacy
        // fallback returns null and must not overwrite a stored value.
        sample({
            clock: getUpsellDataFx.doneData,
            filter: ({ context }) => context !== null,
            fn: ({ context }) => ({ key: UPSELLS_CONTEXT_KEY, value: JSON.stringify(context) }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: getUpsellDataFx.doneData,
            source: { paymentSystem: this.subscriptionsModule.stores.$paymentSystem },
            filter: (_, { context }) => context !== null,
            fn: ({ paymentSystem }, { context, items }) => ({
                context: context!,
                paymentSystem,
                upsells: items.map(({ upsellId }) => upsellId)
            }),
            target: upsellSequenceResolved
        });

        // A language switch must reach users who are already mid-chain, whose
        // config is frozen in metadata. Refetch the config and replace ONLY the
        // content map: `upsells` and upsell_progress_index stay untouched, so
        // the chain and the user's position in it never move.
        sample({
            clock: contentLocaleChanged,
            source: {
                metadata: this.metadataModule.stores.$metadata,
                experimentClient: this.amplitudeFlagsModule.stores.$experimentClient
            },
            filter: (s) =>
                s.metadata.upsells !== undefined &&
                s.metadata[UPSELLS_VIEW_DATA_LOCALE_KEY] !== getContentLocale(),
            fn: (s) => s.experimentClient,
            target: refreshUpsellViewDataFx
        });

        sample({
            clock: refreshUpsellViewDataFx.doneData,
            fn: ({ viewData }) => ({ key: UPSELLS_VIEW_DATA_KEY, value: JSON.stringify(viewData) }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: refreshUpsellViewDataFx.doneData,
            fn: ({ locale }) => ({ key: UPSELLS_VIEW_DATA_LOCALE_KEY, value: locale }),
            target: this.metadataModule.events.setMetadataRequested
        });

        // A language switch can happen while a config fetch is in flight: the
        // landed blob then belongs to the OLD locale, and nothing would ever
        // refetch it — the switch event already fired and was swallowed by the
        // stamp filter above. Converge instead: whenever a landed fetch does
        // not match the current locale, fetch once more (each retry reads the
        // live cookie, so a stable locale terminates the loop).
        sample({
            clock: [getUpsellDataFx.doneData, refreshUpsellViewDataFx.doneData],
            source: { experimentClient: this.amplitudeFlagsModule.stores.$experimentClient },
            filter: (_, done) => done.locale !== getContentLocale(),
            fn: (s) => s.experimentClient,
            target: refreshUpsellViewDataFx
        });

        // Ecommpay keeps its hardcoded first-step redirect (legacy 31k pack).
        // Truegate's first step depends on the backend sequence + per-currency
        // resolution, so its redirect is handled by the generic first-login
        // redirect (clock: [$firstLoginFlag, $upsells]) once metadata is written.
        sample({
            clock: upsellSequenceInitialized,
            source: {
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                isApp: this.platformContextModule.stores.$isApp
            },
            filter: (s) =>
                !s.isApp && !s.isUpsellRoute && s.paymentSystem === "ecommpay" && s.subscriptions.length <= 1,
            fn: () => "/upsell/30k_prompts_complete_pack",
            target: this.routerModule.events.routerReplace
        });

        sample({
            clock: upsellSequenceInitialized,
            source: {
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                isApp: this.platformContextModule.stores.$isApp
            },
            filter: (s) =>
                !s.isApp &&
                !s.isUpsellRoute &&
                ["ecommpay", "truegate"].includes(s.paymentSystem) &&
                s.subscriptions.length <= 1,
            target: enableFirstLoginFlowFx
        });

        sample({
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                metadata: this.metadataModule.stores.$metadata,
                currentUpsellId: $currentUpsellId,
                completeBundlePrereqsUnpurchased: $completeBundlePrereqsUnpurchased,
                firstLoginFlag: $firstLoginFlag,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive
            },
            filter: (s) => s.paymentSystem === "solidgate",
            fn: ({ metadata, currentUpsellId, completeBundlePrereqsUnpurchased, firstLoginFlag, isUpsellRoute }) => {
                // The swap only reorders funnel SCREENS, so it must not reach
                // outside the funnel: home/profile build their product cards from
                // this same store, and dropping 600_prompts from it hides the
                // Personal Boost card there.
                const upsells = applyCompleteBundleSequence(
                    parseUpsellsMetadata(metadata.upsells),
                    isUpsellRoute && firstLoginFlag && completeBundlePrereqsUnpurchased === true,
                    currentUpsellId
                );
                const shouldInjectBundle = currentUpsellId === BUNDLE_UPSELL_ID;
                const hasBundle = upsells.some((upsell) => upsell.upsellId === BUNDLE_UPSELL_ID);

                if (shouldInjectBundle && !hasBundle) {
                    return [...upsells, { upsellId: BUNDLE_UPSELL_ID, planId: BUNDLE_PLAN_ID }];
                }

                return upsells;
            },
            target: $upsells
        });

        sample({
            source: this.metadataModule.stores.$metadata,
            fn: (metadata) => parseUpsellViewDataMetadata(metadata[UPSELLS_VIEW_DATA_KEY]),
            target: $upsellViewData
        });

        // {{price}} mirrors what the payment blocks display: the offer's price
        // in the subscription currency (solidgate / apple / truegate), or the
        // ecommpay plan's for the legacy ecommpay chain.
        sample({
            source: {
                viewData: $currentUpsellViewData,
                currentUpsellId: $currentUpsellId,
                planByTag: this.paymentModule.stores.$currentPlanByTag,
                offer: $currentUpsellOffer
            },
            fn: ({ viewData, currentUpsellId, planByTag, offer }) => {
                const amount = offer ? offer.price : (planByTag?.newPrice ?? null);
                const currency = offer ? offer.currency : planByTag?.currency;
                const price =
                    amount != null && currency
                        ? bidiIsolate(`${getCurrencySymbol(currency)}${formatMinorINRRule(amount / 100, currency)}`)
                        : undefined;
                return resolveDisplayViewData(viewData, currentUpsellId, price, offer?.discountPercent);
            },
            target: $currentUpsellViewDataResolved
        });

        sample({
            source: this.metadataModule.stores.$metadata,
            fn: (metadata) => parseUpsellContextMetadata(metadata[UPSELLS_CONTEXT_KEY]),
            target: $upsellContext
        });

        // Resolve the Complete Bundle precondition ("first two funnel upsells not
        // purchased") while the user is anywhere in the upsell funnel.
        //
        // Two separate triggers on purpose. This one is the INITIAL resolve, and
        // its clock lists EVERY store the filter reads, not just route entry: on a
        // page refresh inside the funnel these load in an arbitrary order, and
        // whichever arrives last must still be able to start the check. With a
        // narrower clock the filter simply rejects every early trigger and the
        // verdict stays `null` forever, so the bundle silently never appears.
        // Guarded on a still unresolved server verdict, so later refreshes of any
        // of these stores cost nothing.
        sample({
            clock: [
                this.routesModule.stores.upsell.opened,
                this.routesModule.stores.upsell.$isActive,
                this.metadataModule.stores.$metadata,
                this.subscriptionsModule.stores.$paymentSystem,
                $firstLoginFlag
            ],
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                metadata: this.metadataModule.stores.$metadata,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                isPending: checkCompleteBundlePrereqsFx.pending,
                byServer: $completeBundlePrereqsUnpurchasedByServer
            },
            filter: (s) =>
                s.byServer === null &&
                s.isUpsellRoute &&
                !s.isPending &&
                ["solidgate", "truegate"].includes(s.paymentSystem) &&
                Boolean(findCompleteBundleEntry(parseUpsellsMetadata(s.metadata.upsells))),
            target: checkCompleteBundlePrereqsFx
        });

        // While the swap is active the replaced screen (600_prompts) is not part
        // of the sequence, so landing on it — browser Back from the bundle, a
        // stale in-funnel URL — would render a screen with no plan and a pack
        // module that never resolves its status (permanently disabled buy
        // button). Send the user to the screen that actually replaced it.
        // Keyed on the screen actually being absent from the sequence rather than
        // on the funnel flags: whenever the swap dropped the current screen the
        // page cannot resolve a plan and sits on the loading state forever.
        sample({
            clock: [currentUpsellIdChanged, $upsells],
            source: {
                currentUpsellId: $currentUpsellId,
                upsells: $upsells
            },
            filter: (s) =>
                s.currentUpsellId !== null &&
                COMPLETE_BUNDLE_REPLACED_IDS.has(s.currentUpsellId) &&
                !s.upsells.some((u) => u.upsellId === s.currentUpsellId) &&
                Boolean(findCompleteBundleEntry(s.upsells)),
            fn: (s) => `/upsell/${findCompleteBundleEntry(s.upsells)!.upsellId}`,
            target: this.routerModule.events.routerReplace
        });

        // Post-charge recheck: charge endpoints write the purchase row
        // synchronously, so an immediate recheck is consistent. Failed charges
        // re-fire it too, to restore the server truth after the optimistic
        // buy-click flip below.
        sample({
            clock: [
                chargeUpsellFx.done,
                upsellUpiPaymentSucceeded,
                chargeAppleUpsellFx.done,
                registerTruegateUpsellFx.done,
                chargeUpsellFx.fail,
                registerTruegateUpsellFx.fail,
                chargeAppleUpsellFx.fail,
                upsellUpiPaymentFailed
            ],
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                metadata: this.metadataModule.stores.$metadata,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                isPending: recheckCompleteBundlePrereqsFx.pending
            },
            filter: (s) =>
                s.isUpsellRoute &&
                !s.isPending &&
                ["solidgate", "truegate"].includes(s.paymentSystem) &&
                Boolean(findCompleteBundleEntry(parseUpsellsMetadata(s.metadata.upsells))),
            target: recheckCompleteBundlePrereqsFx
        });

        // Optimistic flip: starting a purchase of one of the first two funnel
        // upsells disqualifies the Complete Bundle right away. Waiting for the
        // post-charge recheck is not enough — the "go to next non-sale"
        // navigation fires on the charge's doneData and would read the
        // not-yet-rebuilt sequence, sending the user to a bundle screen they no
        // longer qualify for.
        sample({
            clock: [upsellBought, upsellUpiPaymentStarted],
            source: { currentUpsellId: $currentUpsellId },
            filter: (s) => s.currentUpsellId !== null && COMPLETE_BUNDLE_PREREQ_IDS.includes(s.currentUpsellId),
            fn: () => true,
            target: $completeBundlePrereqPurchaseStarted
        });

        // A failed charge means nothing was bought — drop the optimistic flip and
        // fall back to the server verdict (rechecked by the sample above).
        sample({
            clock: [
                chargeUpsellFx.fail,
                upsellChargeDeclined,
                upsell3dsChallengeFailed,
                registerTruegateUpsellFx.fail,
                chargeAppleUpsellFx.fail,
                upsellUpiPaymentFailed
            ],
            fn: () => false,
            target: $completeBundlePrereqPurchaseStarted
        });

        sample({
            clock: [checkCompleteBundlePrereqsFx.doneData, recheckCompleteBundlePrereqsFx.doneData],
            // A prerequisite whose Pix / UPI order can still be paid is no
            // longer "unpurchased": the bundle would sell it a second time.
            fn: (data) =>
                COMPLETE_BUNDLE_PREREQ_IDS.every(
                    (upsellId) =>
                        checkUpsellPurchased(data, upsellId) === "Fail" && !isUpsellAwaitingPayment(data, upsellId)
                ),
            target: $completeBundlePrereqsUnpurchasedByServer
        });

        // The post-charge recheck is the authoritative answer about that charge:
        // a declined card still returns HTTP success, so `chargeUpsellFx.done`
        // alone must not keep the optimistic flag set forever.
        // Not while a 3DS challenge is open: the charge returned 3ds_verify, the
        // row reads as "Fail" until the bank answers, and clearing the flip here
        // would swap the Complete Bundle in for a purchase that is about to
        // succeed. A failed or dismissed challenge clears the flip itself.
        sample({
            clock: recheckCompleteBundlePrereqsFx.doneData,
            source: $upsell3dsChallenge,
            filter: (challenge) => challenge === null,
            fn: () => false,
            target: $completeBundlePrereqPurchaseStarted
        });


        sample({
            clock: this.metadataModule.effects.getMetadataFx.doneData,
            fn: () => true,
            target: $metadataLoaded
        });

        sample({
            source: combine({
                metadataLoaded: $metadataLoaded,
                metadata: this.metadataModule.stores.$metadata,
                firstLoginFlag: $firstLoginFlag,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            }),
            fn: (s) => {
                if (s.isUpsellRoute) return false;
                if (s.paymentSystem === "other_payment_system" || s.paymentSystem === "apple") return false;
                if (s.firstLoginFlag) return true;
                if (s.metadataLoaded && s.metadata.upsells === undefined) return true;
                return false;
            },
            target: $isFirstEntryPending
        });

        const $upsellFallbackReady = combine({
            metadataLoaded: $metadataLoaded,
            firstLoginLoaded: $firstLoginFlagLoaded,
            student: this.authModule.stores.$student.map(Boolean),
            paymentSystem: this.subscriptionsModule.stores.$paymentSystem.map(Boolean),
            // The chain is frozen for good by the first request, and the backend
            // freezes the profession_upsells arm from the assignments sent with
            // it — so they must already be the signed-in identity's.
            flagsSettled: this.amplitudeFlagsModule.stores.$identityFlagsSettled,
            isUpsellRoute: this.routesModule.stores.upsell.$isActive,
            isAssistantRoute: this.routesModule.stores.assistant.$isActive,
            isProfileRoute: this.routesModule.stores.profile.$isActive,
            isPromptsLibraryRoute: this.routesModule.stores.promptsLibrary.$isActive,
            isLessonRoute: this.routesModule.stores.lesson.$isActive
        }).map((r) => r.metadataLoaded && r.firstLoginLoaded && r.student && r.paymentSystem && r.flagsSettled);

        sample({
            clock: $upsellFallbackReady,
            source: {
                metadata: this.metadataModule.stores.$metadata,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                experimentClient: this.amplitudeFlagsModule.stores.$experimentClient
            },
            filter: (s, ready) =>
                !!ready &&
                ["solidgate", "truegate"].includes(s.paymentSystem) &&
                (s.metadata.upsells === undefined || shouldReloadUpsells(s.metadata, s.paymentSystem)),
            // The experiment client is null without PostHog; the settled flag is
            // released at once then and the request goes without assignments.
            fn: (s) => s.experimentClient,
            target: getUpsellDataFx
        });

        // The experiment exposure, tracked only for students the arm makes a
        // difference to (the backend leaves the arm empty for the rest).
        sample({
            clock: upsellSequenceResolved,
            source: this.amplitudeFlagsModule.stores.$experimentClient,
            filter: (client, { context }) => client !== null && Boolean(context.professionUpsells),
            fn: (client) => client!,
            target: trackProfessionUpsellsExposureFx
        });

        sample({
            clock: [
                this.routesModule.stores.profile.opened,
                this.routesModule.stores.profile.changed,
                this.routesModule.stores.assistant.opened,
                this.routesModule.stores.promptsLibrary.opened,
                this.routesModule.stores.promptsLibrary.changed,
                this.routesModule.stores.home.opened,
                this.routesModule.stores.upsell.opened,
                this.routesModule.stores.aiTools.opened,
                this.routesModule.stores.lesson.opened
            ],
            target: this.metadataModule.events.refreshMetadataRequested
        });

        sample({
            clock: [this.routesModule.stores.assistant.opened, this.routesModule.stores.upsell.opened],
            target: [getFromProfileFlagFx]
        });

        // The upsell routes load no provider catalog. Everything a screen and
        // the charge need — the resolved plan for the student's provider and
        // currency, its price, the admin's old price and discount, the display
        // name in the student's language — is one payload,
        // /students/upsells-prices: request it on entry to the upsell routes
        // and again once the content locale moves past the one it was fetched
        // for. (The legacy ecommpay chain keeps its own plan list.)
        sample({
            clock: [
                this.routesModule.stores.upsell.$isActive,
                this.routesModule.stores.assistant.$isActive,
                this.routesModule.stores.aiTools.$isActive,
                this.routesModule.stores.lesson.$isActive,
                $upsells,
                contentLocaleChanged
            ],
            source: {
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive,
                fetchedLocale: $upsellPricesLocale,
                pending: getUpsellPricesFx.pending
            },
            filter: (s) =>
                (s.isUpsellRoute || s.isAssistantRoute || s.isAiToolsRoute || s.isLessonRoute) &&
                !s.pending &&
                s.fetchedLocale !== getContentLocale(),
            target: getUpsellPricesFx
        });

        // Hard guard: never let a bundle charge request fire when the bundle is
        // already purchased, or before the purchase status has been resolved
        // (fail-safe). Non-bundle upsells pass through untouched. This is the
        // primary, UI-independent protection against double-buying the bundle.
        sample({
            clock: upsellBought,
            source: {
                currentUpsellId: $currentUpsellId,
                bundleAllPacksPurchased: $bundleAllPacksPurchased,
                isBundleStatusResolved: $isBundlePurchaseStatusResolved,
                isApp: this.platformContextModule.stores.$isApp,
                // A charge already in flight: a double-click on Buy used to
                // send two charges (and charge the card twice) because the
                // server-side "already purchased" row only exists after the
                // first one returns.
                isChargePending: $isUpsellRegistrationPending,
                // The previous charge of this very upsell is still waiting for
                // its verdict or its bank dialog: a second order now could end
                // up charging the card twice once both are confirmed.
                awaitingVerdict: $upsellIdsAwaitingVerdict
            },
            filter: (s) =>
                !s.isApp &&
                !s.isChargePending &&
                !(s.currentUpsellId !== null && s.awaitingVerdict.includes(s.currentUpsellId)) &&
                !(s.currentUpsellId === BUNDLE_UPSELL_ID && (s.bundleAllPacksPurchased || !s.isBundleStatusResolved)),
            target: upsellChargeRequested
        });

        sample({
            clock: upsellBought,
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                failedAttempts: $upsellFailedAttemptsCount
            },
            filter: (s) =>
                ["solidgate", "ecommpay", "truegate"].includes(s.paymentSystem) &&
                s.failedAttempts >= MAX_UPSELL_FAIL_ATTEMPTS,
            target: upsellPurchaseLimitReached
        });

        sample({
            clock: [checkUpsellPurchasedFx.doneData, upsellApmAwaitingChecked],
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                upsells: $upsells,
                failedAttempts: $upsellFailedAttemptsCount,
                failedPaymentIds: $upsellFailedPaymentIds,
                threeDsOrderIds: $upsell3dsOrderIds
            },
            filter: (s, data) => {
                if (!["solidgate", "ecommpay", "truegate"].includes(s.paymentSystem)) return false;
                if (s.upsells.length === 0) return false;
                if (s.failedAttempts >= MAX_UPSELL_FAIL_ATTEMPTS) return false;

                const entries = Object.entries(data.productStatus);
                for (const [upsellId, status] of entries) {
                    if (!status?.paymentId) continue;
                    // 3DS challenges are not card declines: skip orders still in
                    // the challenge and orders known to have gone through one.
                    if (status.orderStatus === UPSELL_3DS_VERIFY_STATUS) continue;
                    // Nor is a Pix / UPI request the customer has not paid yet.
                    if (isAwaitingPaymentOrderStatus(status.orderStatus)) continue;
                    if (s.threeDsOrderIds.includes(status.paymentId)) continue;
                    const groupId = UPSELL_GROUPS[upsellId] ?? upsellId;
                    // A payment id already counted towards the limit is never
                    // counted again, regardless of which group variant it came from.
                    if (s.failedPaymentIds[groupId]?.includes(status.paymentId)) continue;
                    const isFail = checkUpsellStatus(status.orderStatus, status.transactionStatus) === "Fail";
                    if (isFail) return true;
                }

                return false;
            },
            fn: (s, data) => {
                // Register every not-yet-counted failed payment id in this
                // response at once (a response can carry fails for several
                // upsells of the same group, e.g. full + sale variants).
                const failedPaymentIds: Record<string, string[]> = { ...s.failedPaymentIds };
                const newFailedPaymentIds: Record<string, string[]> = {};
                let groupId = "";
                let paymentId = "";
                const entries = Object.entries(data.productStatus);
                for (const [upsellId, status] of entries) {
                    if (!status?.paymentId) continue;
                    // 3DS challenges are not card declines: skip orders still in
                    // the challenge and orders known to have gone through one.
                    if (status.orderStatus === UPSELL_3DS_VERIFY_STATUS) continue;
                    // Nor is a Pix / UPI request the customer has not paid yet.
                    if (isAwaitingPaymentOrderStatus(status.orderStatus)) continue;
                    if (s.threeDsOrderIds.includes(status.paymentId)) continue;
                    const candidateGroupId = UPSELL_GROUPS[upsellId] ?? upsellId;
                    if (failedPaymentIds[candidateGroupId]?.includes(status.paymentId)) continue;
                    const isFail = checkUpsellStatus(status.orderStatus, status.transactionStatus) === "Fail";
                    if (isFail) {
                        newFailedPaymentIds[upsellId] = [...(newFailedPaymentIds[upsellId] ?? []), status.paymentId];
                        failedPaymentIds[candidateGroupId] = [
                            ...(failedPaymentIds[candidateGroupId] ?? []),
                            status.paymentId
                        ];
                        groupId = candidateGroupId;
                        paymentId = status.paymentId;
                    }
                }
                return {
                    count: countFailedPaymentIds(failedPaymentIds),
                    groupId,
                    paymentId,
                    failedPaymentIds,
                    newFailedPaymentIds
                };
            },
            target: upsellFailedAttemptRegistered
        });

        sample({
            clock: upsellFailedAttemptRegistered,
            fn: ({ count }) => ({
                key: UPSELL_FAILED_ATTEMPTS_KEY,
                value: String(count)
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: upsellFailedAttemptRegistered,
            fn: ({ failedPaymentIds }) => ({
                key: UPSELL_FAILED_PAYMENT_IDS_KEY,
                value: JSON.stringify(failedPaymentIds)
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        // The form's own fail message is about the form's order; a failure that
        // names its order (one the page waited on) was counted by the wait's check.
        sample({
            clock: upsellUpiPaymentFailed,
            source: {
                formResponse: $upsellUpiFormResponse,
                currentUpsellId: $currentUpsellId,
                failedAttempts: $upsellFailedAttemptsCount,
                failedPaymentIds: $upsellFailedPaymentIds
            },
            filter: (state, failure) => {
                if (apmPaymentFailureOf(failure).counted) return false;
                const paymentId = apmPaymentFailureOf(failure).orderId ?? state.formResponse?.order_id;
                const groupId = state.currentUpsellId
                    ? (UPSELL_GROUPS[state.currentUpsellId] ?? state.currentUpsellId)
                    : "";

                return (
                    Boolean(paymentId) &&
                    Boolean(groupId) &&
                    state.failedAttempts < MAX_UPSELL_FAIL_ATTEMPTS &&
                    !state.failedPaymentIds[groupId]?.includes(paymentId!)
                );
            },
            fn: (state, failure) => {
                const groupId = UPSELL_GROUPS[state.currentUpsellId!] ?? state.currentUpsellId!;
                const paymentId = apmPaymentFailureOf(failure).orderId ?? state.formResponse!.order_id!;
                const failedPaymentIds = {
                    ...state.failedPaymentIds,
                    [groupId]: [...(state.failedPaymentIds[groupId] ?? []), paymentId]
                };

                return {
                    count: countFailedPaymentIds(failedPaymentIds),
                    groupId,
                    paymentId,
                    failedPaymentIds,
                    newFailedPaymentIds: { [state.currentUpsellId!]: [paymentId] }
                };
            },
            target: upsellFailedAttemptRegistered
        });

        // Every charge below works from the current upsell's OFFER — the
        // server-resolved item of /students/upsells-prices (plan id, currency,
        // price) — not from a plan object: the upsell routes load no catalog.
        sample({
            clock: upsellUpiFormRequested,
            source: {
                formResponse: $upsellUpiFormResponse,
                formProductId: $upsellUpiFormProductId,
                formPending: $isUpsellUpiFormPending,
                currentUpsellId: $currentUpsellId,
                offer: $currentUpsellOffer,
                userCountry: this.paymentModule.stores.$userCountry,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                hasRecurringToken: this.subscriptionsModule.stores.$solidgateUpsellHasRecurringToken,
                fromProfile: $fromProfile,
                firstLoginFlag: $firstLoginFlag,
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive,
                history: this.routerModule.stores.$history,
                bundleAllPacksPurchased: $bundleAllPacksPurchased,
                isBundleStatusResolved: $isBundlePurchaseStatusResolved,
                failedAttempts: $upsellFailedAttemptsCount
            },
            filter: (state) => {
                const currency = state.offer?.currency;
                const bundleBlocked =
                    state.currentUpsellId === BUNDLE_UPSELL_ID &&
                    (state.bundleAllPacksPurchased || !state.isBundleStatusResolved);

                return (
                    (state.formResponse === null || state.formProductId !== state.offer?.planId) &&
                    !state.formPending &&
                    state.currentUpsellId !== null &&
                    state.offer !== null &&
                    state.failedAttempts < MAX_UPSELL_FAIL_ATTEMPTS &&
                    !bundleBlocked &&
                    resolveUpsellApmMethod({
                        paymentSystem: state.paymentSystem,
                        currency,
                        userCountry: state.userCountry,
                        subscriptions: state.subscriptions,
                        hasRecurringToken: state.hasRecurringToken
                    }) !== null
                );
            },
            fn: (state) => {
                const method = resolveUpsellApmMethod({
                    paymentSystem: state.paymentSystem,
                    currency: state.offer?.currency,
                    userCountry: state.userCountry,
                    subscriptions: state.subscriptions,
                    hasRecurringToken: state.hasRecurringToken
                })!;
                return {
                    upsellId: state.currentUpsellId!,
                    productId: state.offer!.planId,
                    method,
                    currency: method === "pix-qr" ? ("BRL" as const) : ("INR" as const),
                    purchasePageURL: typeof window !== "undefined" ? window.location.href : "",
                    placement: resolveUpsellPlacement({
                        planId: state.offer!.planId,
                        upsellId: state.currentUpsellId,
                        fromProfile: state.fromProfile,
                        firstLoginFlag: state.firstLoginFlag,
                        isAssistantRoute: state.isAssistantRoute,
                        isAiToolsRoute: state.isAiToolsRoute,
                        isLessonRoute: state.isLessonRoute,
                        history: state.history
                    })
                };
            },
            target: createUpsellUpiFormFx
        });

        sample({
            clock: upsellChargeRequested,
            source: {
                offer: $currentUpsellOffer,
                currentUpsellId: $currentUpsellId,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                failedAttempts: $upsellFailedAttemptsCount,
                fromProfile: $fromProfile,
                firstLoginFlag: $firstLoginFlag,
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive,
                history: this.routerModule.stores.$history
            },
            filter: (s) =>
                s.offer !== null &&
                Boolean(s.offer.currency) &&
                s.paymentSystem === "solidgate" &&
                s.failedAttempts < MAX_UPSELL_FAIL_ATTEMPTS,
            fn: (s) => {
                return {
                    productId: s.offer!.planId,
                    currency: s.offer!.currency!,
                    purchasePageURL: typeof window !== "undefined" ? window.location.href : "",
                    placement: resolveUpsellPlacement({
                        planId: s.offer!.planId,
                        upsellId: s.currentUpsellId,
                        fromProfile: s.fromProfile,
                        firstLoginFlag: s.firstLoginFlag,
                        isAssistantRoute: s.isAssistantRoute,
                        isAiToolsRoute: s.isAiToolsRoute,
                        isLessonRoute: s.isLessonRoute,
                        history: s.history
                    })
                };
            },

            target: chargeUpsellFx
        });

        // ECOMMPAY: handle upsell buy by registering upsell with planCode (resolve from plan/tag)
        sample({
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isProfileRoute: this.routesModule.stores.profile.$isActive,
                isHomeRoute: this.routesModule.stores.home.$isActive,
                isPromptsLibraryRoute: this.routesModule.stores.promptsLibrary.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive
            },
            filter: (s) =>
                s.paymentSystem === "ecommpay" &&
                (s.isUpsellRoute ||
                    s.isAssistantRoute ||
                    s.isProfileRoute ||
                    s.isHomeRoute ||
                    s.isPromptsLibraryRoute ||
                    s.isAiToolsRoute),
            target: this.paymentModule.events.getEcommpayAnonPlans
        });

        sample({
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                ecommpayPlans: this.paymentModule.stores.$ecommpayPlans,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                upsells: $upsells,
                metadata: this.metadataModule.stores.$metadata,
                metadataLoaded: $metadataLoaded
            },
            filter: (s) =>
                s.metadataLoaded &&
                s.paymentSystem === "ecommpay" &&
                s.ecommpayPlans.length > 0 &&
                s.subscriptions.length > 0 &&
                (s.upsells.length === 0 || shouldReloadUpsells(s.metadata, s.paymentSystem)) &&
                (!Boolean(s.metadata.upsells) || shouldReloadUpsells(s.metadata, s.paymentSystem)),
            fn: (s) => {
                const rawCurrency = s.subscriptions?.[0]?.currency || s.subscriptions?.[0]?.plan?.currency || "";
                const currency = rawCurrency.trim().toUpperCase();
                const resolveTagToPlanCode = (tag: string): string => {
                    const candidates = s.ecommpayPlans.filter((p) => p.tags.includes(tag));
                    if (!candidates.length || !currency) return "";
                    const byCurrency = candidates.find((p) => (p.currency ?? "").trim().toUpperCase() === currency);
                    return byCurrency?.code ?? "";
                };

                return {
                    key: "upsells",
                    value: JSON.stringify([
                        {
                            upsellId: "30k_prompts_complete_pack",
                            planId: resolveTagToPlanCode("30k_prompts_complete_pack")
                        },
                        {
                            upsellId: "30k_prompts_complete_pack_sale",
                            planId: resolveTagToPlanCode("30k_prompts_complete_pack_sale")
                        },
                        { upsellId: "ai_tools", planId: resolveTagToPlanCode("ai_tools") },
                        { upsellId: "ai_tools_sale", planId: resolveTagToPlanCode("ai_tools_sale") },
                        // Claude Work Bundle is a Solidgate/Truegate funnel step; keep it
                        // out of the ecommpay sequence (ecommpay is intentionally left
                        // on the legacy 31k complete pack sequence).
                        { upsellId: "600_prompts", planId: resolveTagToPlanCode("600_prompts") }
                    ])
                };
            },
            target: [this.metadataModule.events.setMetadataRequested, upsellSequenceInitialized]
        });

        sample({
            clock: upsellSequenceInitialized,
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            filter: (s) => s.paymentSystem === "ecommpay",
            fn: () => ({
                key: UPSELLS_PAYMENT_SYSTEM_KEY,
                value: "ecommpay"
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                metadata: this.metadataModule.stores.$metadata
            },
            filter: (s) => s.paymentSystem === "ecommpay",
            fn: ({ metadata }) => {
                if (metadata.upsells) {
                    try {
                        return JSON.parse(metadata.upsells);
                    } catch { }
                } else return [];
            },
            target: $upsells
        });

        sample({
            clock: upsellChargeRequested,
            source: {
                offer: $currentUpsellOffer,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                upsellQuery: this.routesModule.stores.upsell.$query
            },
            filter: (s) => s.offer !== null && s.paymentSystem === "apple" && !Boolean(s.upsellQuery.email?.trim()),
            fn: (s) => {
                return {
                    productId: s.offer!.planId
                };
            },

            target: chargeAppleUpsellFx
        });

        sample({
            clock: [
                $currentUpsellId,
                this.paymentModule.stores.$ecommpayPlans,
                $upsells,
                this.appModule.events.routePathNameChanged
            ],
            source: {
                plans: this.paymentModule.stores.$ecommpayPlans,
                currentUpsellId: $currentUpsellId,
                upsells: $upsells,
                profilePath: this.routesModule.stores.profile.$path,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            filter: (s) => {
                if (s.profilePath?.includes("/profile/settings/subscriptions/renew")) return false;
                return (
                    s.currentUpsellId !== null &&
                    s.upsells.length > 0 &&
                    s.plans.length > 0 &&
                    s.paymentSystem === "ecommpay"
                );
            },
            fn: (s) => {
                const upsell = s.upsells.find((u) => u.upsellId === s.currentUpsellId);
                const planId = upsell?.planId ?? "";
                const plan = s.plans.find((p) => p.code === planId) || null;
                return plan as any;
            },
            target: this.paymentModule.events.setPlan
        });

        sample({
            clock: upsellChargeRequested,
            source: {
                currentPlanByTag: this.paymentModule.stores.$currentPlanByTag,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                failedAttempts: $upsellFailedAttemptsCount,
                fromProfile: $fromProfile,
                firstLoginFlag: $firstLoginFlag,
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive,
                history: this.routerModule.stores.$history
            },
            filter: (s) =>
                s.currentPlanByTag !== null &&
                s.paymentSystem === "ecommpay" &&
                s.failedAttempts < MAX_UPSELL_FAIL_ATTEMPTS,
            fn: (s) => {
                const prevUrl = s.history?.length > 1 ? (s.history.at(-2) ?? "") : "";

                let placement = UpsellPlacement.Link;

                if (s.isAssistantRoute || s.isAiToolsRoute) {
                    placement = UpsellPlacement.Assistant;
                } else if (s.isLessonRoute) {
                    placement = UpsellPlacement.Lesson;
                } else if (s.firstLoginFlag) {
                    placement = UpsellPlacement.FirstLogin;
                } else if ((!prevUrl && !s.fromProfile) || prevUrl.includes("/auth/signin")) {
                    placement = UpsellPlacement.Link;
                } else if (Boolean(s.fromProfile)) {
                    placement = UpsellPlacement.PromptsLibrary;
                }

                return { id: s.currentPlanByTag!.code, placement: placement };
            },
            target: registerEcommpayUpsellFx
        });

        // TRUEGATE: freeze the backend-owned funnel sequence into upsell metadata
        // as the config hands it over. The admin-assigned plan id (a truegate
        // plan_code or, the normal case, a catalog TAG) travels as is: the
        // per-currency resolution — tag + subscription currency → concrete code
        // and numeric id — happens on the server, in /students/upsells-prices,
        // which every screen and the charge read. No catalog is loaded here.
        sample({
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                upsells: $upsells,
                metadata: this.metadataModule.stores.$metadata,
                metadataLoaded: $metadataLoaded,
                configItems: $configUpsellItems
            },
            filter: (s) =>
                s.metadataLoaded &&
                s.paymentSystem === "truegate" &&
                s.subscriptions.length > 0 &&
                s.configItems.length > 0 &&
                (s.upsells.length === 0 || shouldReloadUpsells(s.metadata, s.paymentSystem)) &&
                (!Boolean(s.metadata.upsells) || shouldReloadUpsells(s.metadata, s.paymentSystem)),
            fn: (s) => ({
                key: "upsells",
                value: JSON.stringify(
                    s.configItems
                        .filter((item) => Boolean(item.upsellId))
                        .map(({ upsellId, planId }) => ({ upsellId, planId: planId || upsellId }))
                )
            }),
            target: [this.metadataModule.events.setMetadataRequested, upsellSequenceInitialized]
        });

        // Stamp the provider next to the truegate chain, mirroring the
        // solidgate write: shouldReloadUpsells uses it to rebuild the chain
        // when the student's payment system changes.
        sample({
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                subscriptions: this.subscriptionsModule.stores.$subscriptions,
                upsells: $upsells,
                metadata: this.metadataModule.stores.$metadata,
                metadataLoaded: $metadataLoaded,
                configItems: $configUpsellItems
            },
            filter: (s) =>
                s.metadataLoaded &&
                s.paymentSystem === "truegate" &&
                s.subscriptions.length > 0 &&
                s.configItems.length > 0 &&
                (s.upsells.length === 0 || shouldReloadUpsells(s.metadata, s.paymentSystem)) &&
                (!Boolean(s.metadata.upsells) || shouldReloadUpsells(s.metadata, s.paymentSystem)),
            fn: () => ({ key: UPSELLS_PAYMENT_SYSTEM_KEY, value: "truegate" }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: upsellSequenceInitialized,
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            filter: (s) => s.paymentSystem === "truegate",
            fn: () => ({
                key: UPSELLS_PAYMENT_SYSTEM_KEY,
                value: "truegate"
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        // TRUEGATE: populate $upsells from metadata. The screens and the charge
        // read the resolved plan from the offer (upsells-prices), so the chain
        // only needs the upsell ids; the bundle entry is injected by its tag.
        sample({
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                metadata: this.metadataModule.stores.$metadata,
                currentUpsellId: $currentUpsellId,
                completeBundlePrereqsUnpurchased: $completeBundlePrereqsUnpurchased,
                firstLoginFlag: $firstLoginFlag,
                isUpsellRoute: this.routesModule.stores.upsell.$isActive
            },
            filter: (s) => s.paymentSystem === "truegate",
            fn: ({ metadata, currentUpsellId, completeBundlePrereqsUnpurchased, firstLoginFlag, isUpsellRoute }) => {
                let upsells: IUpsellData[] = [];
                if (metadata.upsells) {
                    try {
                        upsells = JSON.parse(metadata.upsells);
                    } catch { }
                }

                // Same third-screen swap as the solidgate path, funnel-only for the
                // same reason.
                upsells = applyCompleteBundleSequence(
                    upsells,
                    isUpsellRoute && firstLoginFlag && completeBundlePrereqsUnpurchased === true,
                    currentUpsellId
                );

                const shouldInjectBundle = currentUpsellId === BUNDLE_UPSELL_ID;
                const hasBundle = upsells.some((u) => u.upsellId === BUNDLE_UPSELL_ID);

                if (shouldInjectBundle && !hasBundle) {
                    upsells = [...upsells, { upsellId: BUNDLE_UPSELL_ID, planId: BUNDLE_TRUEGATE_TAG }];
                }

                return upsells;
            },
            target: $upsells
        });

        // TRUEGATE: trigger charge on upsellChargeRequested (gated)
        // The truegate charge takes the numeric subscription_plan id, which the
        // offer carries (`truegatePlanId`): the tag → code → id resolution now
        // happens on the server, per subscription currency.
        sample({
            clock: upsellChargeRequested,
            source: {
                offer: $currentUpsellOffer,
                currentUpsellId: $currentUpsellId,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                failedAttempts: $upsellFailedAttemptsCount,
                fromProfile: $fromProfile,
                firstLoginFlag: $firstLoginFlag,
                isAssistantRoute: this.routesModule.stores.assistant.$isActive,
                isAiToolsRoute: this.routesModule.stores.aiTools.$isActive,
                isLessonRoute: this.routesModule.stores.lesson.$isActive,
                history: this.routerModule.stores.$history
            },
            filter: (s) =>
                s.offer !== null &&
                typeof s.offer.truegatePlanId === "number" &&
                s.paymentSystem === "truegate" &&
                s.failedAttempts < MAX_UPSELL_FAIL_ATTEMPTS,
            fn: (s) => ({
                planId: s.offer!.truegatePlanId!,
                placement: resolveUpsellPlacement({
                    planId: s.offer!.planId,
                    upsellId: s.currentUpsellId,
                    fromProfile: s.fromProfile,
                    firstLoginFlag: s.firstLoginFlag,
                    isAssistantRoute: s.isAssistantRoute,
                    isAiToolsRoute: s.isAiToolsRoute,
                    isLessonRoute: s.isLessonRoute,
                    history: s.history
                })
            }),
            target: registerTruegateUpsellFx
        });

        sample({
            clock: getAvailableSubscriptionTransitionsRequested,
            target: getAvailableSubscriptionTransitionsFx
        });

        sample({
            clock: updateSubscriptionButtonClicked,
            target: handleUpdateSubscriptionsFx
        });

        sample({
            clock: handleUpdateSubscriptionsFx.done,
            fn: ({ params }) => ({
                key: "updateSubscriptionProductID",
                value: params
            }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: [
                this.appModule.events.routePathNameChanged,
                this.metadataModule.effects.getMetadataFx.doneData,
                getAvailableSubscriptionTransitionsFx.doneData
            ],
            source: {
                metadata: this.metadataModule.stores.$metadata,
                availableTransitions: $availableSubscriptionTransitions
            },
            filter: (s) => !s.metadata.updateSubscriptionProductID && s.availableTransitions.length > 0,
            fn: () => true,
            target: $isUpdateSubscriptionButtonVisible
        });

        sample({
            clock: [this.metadataModule.effects.getMetadataFx.doneData, handleUpdateSubscriptionsFx.done],
            source: {
                metadata: this.metadataModule.stores.$metadata
            },
            filter: (s) => !!s.metadata.updateSubscriptionProductID,
            fn: () => false,
            target: $isUpdateSubscriptionButtonVisible
        });

        sample({
            clock: handleUpdateSubscriptionsFx.doneData,
            fn: () => "/home",
            target: this.routerModule.events.routerReplace
        });

        sample({
            clock: handleUpdateSubscriptionsFx.done,
            fn: ({ params }) => params,
            target: checkSubscriptionUpdateStatusFx
        });

        sample({
            clock: subscriptionUpdatePollingTick,
            source: $subscriptionUpdateProductId,
            filter: (productId) => productId !== null,
            fn: (productId) => productId!,
            target: checkSubscriptionUpdateStatusFx
        });

        sample({
            clock: checkSubscriptionUpdateStatusFx.doneData,
            filter: (status) => status === "active" || status === "redemption",
            target: [subscriptionUpdateSuccessModalOpened, stopSubscriptionUpdatePolling, clearSubscriptionUpdateData]
        });

        sample({
            clock: subscriptionUpdateSuccessModalClosed,
            target: stopSubscriptionUpdatePolling
        });

        sample({
            clock: $upsellRequestError,
            fn: () => `/home`,
            target: this.routerModule.events.routerReplace
        });

        sample({
            clock: this.routesModule.stores.experiment.opened,
            source: this.routesModule.stores.experiment.$query,
            filter: (query) => Boolean(query.email?.trim()),
            fn: (query) => query.email!.trim(),
            target: this.signInFormModule.events.emailChanged
        });

        // The counter is derived from the set of counted payment ids (one real
        // charge attempt = one id = one increment). The plain numeric metadata
        // key is only a fallback for legacy accounts that have a count but no
        // stored payment ids. This also self-heals accounts whose counter was
        // inflated by the old per-group single-id dedup bug.
        sample({
            clock: this.metadataModule.stores.$metadata,
            fn: (metadata) => {
                const ids = parseFailedPaymentIds(metadata[UPSELL_FAILED_PAYMENT_IDS_KEY]);
                if (ids !== null) return countFailedPaymentIds(ids);
                const parsed = Number(metadata[UPSELL_FAILED_ATTEMPTS_KEY]);
                return Number.isNaN(parsed) ? 0 : parsed;
            },
            target: $upsellFailedAttemptsCount
        });

        sample({
            clock: this.metadataModule.stores.$metadata,
            fn: (metadata) => parseFailedPaymentIds(metadata[UPSELL_FAILED_PAYMENT_IDS_KEY]) ?? {},
            target: $upsellFailedPaymentIds
        });

        sample({
            clock: upsellFailedAttemptRegistered,
            fn: ({ count }) => count,
            target: $upsellFailedAttemptsCount
        });

        sample({
            clock: upsellFailedAttemptRegistered,
            fn: ({ failedPaymentIds }) => failedPaymentIds,
            target: $upsellFailedPaymentIds
        });

        // An upsell that is not in the student's chain and has no offer is not
        // for them: leave for the page the pack lives on.
        sample({
            source: {
                isUpsellRoute: this.routesModule.stores.upsell.$isActive,
                currentUpsellId: $currentUpsellId,
                upsells: $upsells,
                currentEcommpayPlan: this.paymentModule.stores.$currentPlanByTag,
                offer: $currentUpsellOffer
            },
            filter: (s) => {
                const isUpsellRoute = s.isUpsellRoute;
                const hasCurrentUpsellId = s.currentUpsellId !== null;
                const hasPlan = s.currentEcommpayPlan !== null || s.offer !== null;
                const hasUpsells = s.upsells.length > 0;
                const upsellNotInList = !s.upsells.some((u) => u.upsellId === s.currentUpsellId);

                return isUpsellRoute && hasCurrentUpsellId && !hasPlan && hasUpsells && upsellNotInList;
            },
            fn: (s) => {
                if (
                    s.currentUpsellId!.includes("30k_prompts") ||
                    s.currentUpsellId!.includes("30k_prompts_complete_pack")
                ) {
                    return "/prompts-library?pack=ultimate_ai";
                }

                if (s.currentUpsellId!.includes("600_prompts")) {
                    return "/prompts-library?pack=personal_boost";
                }

                if (s.currentUpsellId!.includes("ai_tools")) {
                    return "/assistant";
                }

                return "/home";
            },
            target: this.routerModule.events.routerReplace
        });

        // Bundle "all packs purchased" check — dedicated effect to avoid
        // cross-contamination with individual pack module handlers.
        sample({
            source: {
                upsells: $upsells,
                upsellPath: this.routesModule.stores.upsell.$path,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                bundleCheckRequested: $bundleCheckRequested,
                subscriptions: this.subscriptionsModule.stores.$subscriptions
            },
            filter: (s) =>
                s.upsellPath !== null &&
                s.upsellPath.includes("bundle") &&
                // hasResolvedBundlePackPlanIds(s.upsells) &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem) &&
                !s.bundleCheckRequested,
            fn: (s) => buildBundleCheckUpsellIds(s.upsells),
            target: [checkBundlePurchaseStatusFx, bundleCheckStarted.prepend(() => { })]
        });

        // Re-run the bundle purchase check on demand (window focus / tab visible).
        // Unlike the initial check above, this ignores `$bundleCheckRequested` so
        // it can refresh `$bundleAllPacksPurchased` after a purchase in another tab.
        sample({
            clock: bundlePurchaseRecheckRequested,
            source: {
                upsells: $upsells,
                upsellPath: this.routesModule.stores.upsell.$path,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                isCheckPending: $isBundlePurchaseCheckPending
            },
            filter: (s) =>
                s.upsellPath !== null &&
                s.upsellPath.includes("bundle") &&
                ["solidgate", "ecommpay", "apple", "truegate"].includes(s.paymentSystem) &&
                !s.isCheckPending,
            fn: (s) => buildBundleCheckUpsellIds(s.upsells),
            target: checkBundlePurchaseStatusFx
        });

        sample({
            clock: checkBundlePurchaseStatusFx.doneData,
            source: {
                upsells: $upsells,
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem
            },
            fn: (s, data: CheckPurchasedProductsResponse) => {
                const isPackPurchased = (targetUpsellIds: Set<string>): boolean => {
                    const upsellIdsToCheck = [...targetUpsellIds, BUNDLE_UPSELL_ID, COMPLETE_BUNDLE_UPSELL_ID];

                    const statuses = upsellIdsToCheck.map((upsellId) => {
                        const status = checkUpsellPurchased(data, upsellId);
                        return { upsellId, status };
                    });

                    return statuses.some((s) => s.status === "Success" || s.status === "Processing");
                };

                const claudeGrantsIncomeAi = upsellsHadClaudeFunnel(s.upsells) && isClaudeBundlePurchased(data);
                const personalBoost = isPackPurchased(BUNDLE_PACK_TARGET_IDS.personalBoost);
                const incomeAi = isPackPurchased(BUNDLE_PACK_TARGET_IDS.incomeAi) || claudeGrantsIncomeAi;
                const aiTools = isPackPurchased(BUNDLE_PACK_TARGET_IDS.aiTools);
                const allPurchased = personalBoost && incomeAi && aiTools;

                return allPurchased;
            },
            target: $bundleAllPacksPurchased
        });

        sample({
            clock: checkBundlePurchaseStatusFx.doneData,
            fn: () => true,
            target: $isBundlePurchaseStatusResolved
        });

        sample({
            clock: [firstLoginFlagReset, this.authModule.events.logoutStudent],
            target: clearUpsellProgressFx
        });

        // Claude funnel (solidgate/truegate): once the first-login sequence is
        // finished, append the 31k complete pack to metadata (not the funnel
        // itself) so /upsell/bundle can price it and home/profile banners can
        // resolve it. The plan id is resolved per payment system.
        sample({
            clock: claudeFunnelMetadataAppendRequested,
            source: {
                paymentSystem: this.subscriptionsModule.stores.$paymentSystem,
                metadata: this.metadataModule.stores.$metadata
            },
            filter: (s) => {
                const upsells = parseUpsellsMetadata(s.metadata.upsells);
                if (!upsellsHadClaudeFunnel(upsells) || upsellsHasCompletePack(upsells)) return false;
                const planId = resolveCompletePackPlanIdForFunnel({
                    paymentSystem: s.paymentSystem,
                    metadataPaymentSystem: s.metadata[UPSELLS_PAYMENT_SYSTEM_KEY]
                });
                return Boolean(planId);
            },
            fn: (s) => {
                const planId = resolveCompletePackPlanIdForFunnel({
                    paymentSystem: s.paymentSystem,
                    metadataPaymentSystem: s.metadata[UPSELLS_PAYMENT_SYSTEM_KEY]
                });
                return appendCompletePackForClaudeFunnel(parseUpsellsMetadata(s.metadata.upsells), planId);
            },
            target: claudeFunnelCompletePackAppended
        });

        sample({
            clock: claudeFunnelCompletePackAppended,
            fn: (upsells) => ({ key: "upsells", value: JSON.stringify(upsells) }),
            target: this.metadataModule.events.setMetadataRequested
        });

        sample({
            clock: claudeFunnelCompletePackAppended,
            target: $upsells
        });

        sample({
            clock: subscribeAgainRequested,
            target: firstLoginFlagReset
        });

        sample({
            clock: this.authModule.events.magicLinkAuthenticated,
            source: { isUpsellRoute: this.routesModule.stores.upsell.$isActive },
            filter: (s) => s.isUpsellRoute,
            target: [this.metadataModule.events.refreshMetadataRequested, getFromProfileFlagFx]
        });

        sample({
            clock: [
                this.authModule.events.logoutStudent,
                this.authModule.events.magicLinkAuthenticated,
                this.routesModule.stores.upsell.closed
            ],
            target: upsellUpiFormReset
        });

        reset({
            clock: [
                this.appModule.events.routePathNameChanged,
                this.authModule.events.logoutStudent,
                this.authModule.events.magicLinkAuthenticated
            ],
            target: [$bundleAllPacksPurchased, $bundleCheckRequested, $isBundlePurchaseStatusResolved]
        });

        reset({
            clock: this.authModule.events.logoutStudent,
            target: [$isFirstEntryPending, $metadataLoaded, $configUpsellItems, $upsellPrices]
        });

        reset({
            clock: this.routesModule.stores.upsell.closed,
            target: [$currentUpsellId]
        });
    }
}
